import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, FlatList, TouchableOpacity, Alert, ActivityIndicator, Modal, Platform, Image, useWindowDimensions } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { instantBookingService, QuickBookOption, InstantBooking } from '../services/instantBookingService';
import { activeCalendarIntegrationService } from '../services/activeCalendarIntegrationService';
import { recurringAppointmentsService } from '../services/recurringAppointmentsService';
import { waitlistService } from '../services/waitlistService';
import { AuthManager, type BusinessHours } from '../utils/authManager';
import realPaymentService from '../services/realPaymentService';
import stripePaymentService from '../services/stripePaymentService';
import braintreeService from '../services/braintreeService';
import { BUSINESS_CATEGORIES, searchCategories } from '../constants/businessCategories';
import { favoritesService } from '../services/favoritesService';
import availabilityService, { TimeSlotWithWorkers } from '../services/availabilityService';
import workerService, { Worker } from '../services/workerService';
import { hybridBusinessSearchService } from '../services/hybridBusinessSearchService';
import AppointmentLimitModal from '../components/AppointmentLimitModal';
import { AppointmentLimitCheck } from '../services/appointmentLimitService';
import MyVehiclesModal from '../components/MyVehiclesModal';
import BookingPaymentActions from '../components/BookingPaymentActions';
import guestBookingConfirmationEmailService from '../services/guestBookingConfirmationEmailService';
import PaymentMethodBrandIcon from '../components/PaymentMethodBrandIcon';
import WebStripeCheckoutModal, {
  type WebStripeCheckoutResult,
} from '../components/WebStripeCheckoutModal';
import {
  warmupPaymentApi,
  type CreateAppointmentIntentInput,
} from '../services/webStripeCheckout';
import type { VehicleProfile } from '../types/vehicle';
import { formatVehicleDetailsForNotes, formatVehicleLabel } from '../types/vehicle';
import {
  fetchBusinessBookingSettings,
  formatLocalDateStr,
  isBusinessOpenOnDate,
} from '../utils/businessHoursUtils';
import { vehicleGarageService } from '../services/vehicleGarageService';
import {
  formatServicePriceShort,
  hasListedServicePrice,
  normalizeStoredServicePrice,
  servicePriceForTotal,
} from '../utils/servicePriceDisplay';
import { CUSTOMER_PAYPAL_PAYMENT_HELP } from '../constants/paymentHelpText';
import { showCenteredAlert } from '../utils/centeredAlert';
import {
  WIDGET_BOOKING_API,
  consumeWidgetProductPick,
  isWidgetEmbedContext,
  shouldSuppressWidgetEmbedSuccessAlert,
} from '../utils/widgetEmbed';
import FulfillmentSelector from '../components/checkout/FulfillmentSelector';
import DeliveryCheckoutPanel from '../components/checkout/DeliveryCheckoutPanel';
import WebStripeDeliveryCheckoutModal, {
  type WebStripeDeliveryCheckoutResult,
} from '../components/WebStripeDeliveryCheckoutModal';
import {
  createDeliveryCheckoutIntents,
  warmupDeliveryApi,
} from '../services/deliveryCheckout';
import type { DeliveryAddress, DeliveryQuoteResult } from '../types/delivery';
import { DELIVERY_ENABLED, DELIVERY_FEES_LABEL } from '../constants/deliveryConfig';
import { formatCentsUsd } from '../types/delivery';
import { getGuestCountry, getGuestZipcode, getGuestZipcodeAsync } from '../utils/guestSession';
import AutomotiveServiceIntakeForm from '../components/AutomotiveServiceIntakeForm';
import { isAutomotiveBusinessCategory } from '../utils/automotiveCategoryUtils';
import {
  appendAutomotiveDetailsToInstructions,
  automotiveIntakeFromVehicle,
  automotiveIntakeToGarageInput,
  emptyAutomotiveIntake,
  MANUAL_VEHICLE_ENTRY,
  mapAutomotiveIntakeToAppointmentFields,
  validateAutomotiveIntake,
  vehicleMatchesAutomotiveIntake,
  type AutomotiveIntakeData,
} from '../types/automotiveIntake';

const calendarService = activeCalendarIntegrationService;

const GUEST_PLACEHOLDER_USER_IDS = new Set(['guest_embed', 'guest_app']);

// Service/product grid: wider on the web widget, fewer columns on the mobile app for readability.
const GRID_NUM_COLUMNS = Platform.OS === 'web' ? 5 : 3;
const GRID_GAP = Platform.OS === 'web' ? 8 : 12;
const GRID_HORIZONTAL_PADDING = 16;

function isRealBookingAccountId(id?: string | null): boolean {
  return !!id && !GUEST_PLACEHOLDER_USER_IDS.has(id);
}

function buildGuestWidgetFallbackSlots(): TimeSlotWithWorkers[] {
  const fallbackWorker = {
    id: 'widget_default_staff',
    name: 'Staff',
    phone: '',
    email: '',
    specialties: '',
    businessId: '',
    createdAt: '',
    isActive: true,
  } as Worker;
  const fallbackTimes = [
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
  ];
  return fallbackTimes.map((time) => ({
    time,
    availableWorkers: [fallbackWorker],
    hasAvailability: true,
    totalCapacity: 1,
    bookedCount: 0,
  }));
}

// Cart Item Interface
interface CartItem {
  id: string;
  product: QuickBookOption;
  quantity: number;
  selectedOptions: Record<string, string>; // optionId -> valueId
  unitPrice: number;
  totalPrice: number; // quantity * (unitPrice + option price modifiers)
}

interface OneClickBookingScreenProps {
  onBack: () => void;
  userId: string;
  user?: any;
  onBookingComplete?: (booking: InstantBooking) => void | Promise<void>;
  preSelectedBusiness?: any; // Business object when booking from favorites
  /** When set with preSelectedBusiness (e.g. site widget), skip to availability after services load. */
  preSelectedServiceId?: string;
  onNavigateToBusinessExploration?: () => void; // Callback to navigate to business exploration
  /** Embedded site widget: browse and book before login; account at confirm. */
  guestMode?: boolean;
  onGuestAuthenticated?: (user: any) => void;
}

const OneClickBookingScreen: React.FC<OneClickBookingScreenProps> = ({
  onBack,
  userId,
  user,
  onBookingComplete,
  preSelectedBusiness,
  preSelectedServiceId,
  onNavigateToBusinessExploration,
  guestMode = false,
  onGuestAuthenticated,
}) => {
  console.log('🔍 OneClickBookingScreen received props:', {
    preSelectedBusiness: preSelectedBusiness ? preSelectedBusiness.name : 'null',
    preSelectedBusinessType: preSelectedBusiness ? (preSelectedBusiness.type || preSelectedBusiness.category) : 'null'
  });

  // Fixed card width for the service/product grid, so a row with fewer items
  // than GRID_NUM_COLUMNS (e.g. a category with only 2 products) doesn't
  // stretch those cards to fill the row — every card stays the same size
  // regardless of how many items are in the current filter.
  const { width: windowWidth } = useWindowDimensions();
  const gridCardWidth =
    (windowWidth - GRID_HORIZONTAL_PADDING * 2 - GRID_GAP * (GRID_NUM_COLUMNS - 1)) / GRID_NUM_COLUMNS;

  // Step 1: Business Category Selection
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categories, setCategories] = useState<any[]>([]);
  
  // Step 2: Service/Product Selection
  const [services, setServices] = useState<QuickBookOption[]>([]);
  const [products, setProducts] = useState<QuickBookOption[]>([]);
  const [selectedService, setSelectedService] = useState<QuickBookOption | null>(null); // Keep for backward compatibility
  const [selectedItems, setSelectedItems] = useState<QuickBookOption[]>([]); // Multi-selection support
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>({}); // Track quantities for each item (itemId -> quantity)
  const [itemSelectedOptions, setItemSelectedOptions] = useState<Record<string, Record<string, string>>>({}); // Track selected options for each item (itemId -> {optionId: valueId})
  const [serviceProductFilter, setServiceProductFilter] = useState<'services' | 'products' | 'all'>('all');
  /** Grid category pill filter (e.g. "Chicken", "Beef") — separate from selectedCategory (business category, step 1). */
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string>('all');
  
  // Step 3: Business Selection
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [favoriteBusinesses, setFavoriteBusinesses] = useState<any[]>([]);
  const [nearbyBusinesses, setNearbyBusinesses] = useState<any[]>([]);
  
  // Step 4: Availability & Booking
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlotWithWorkers[]>([]);
  const [selectedWorker, setSelectedWorker] = useState<Worker | null>(null);
  const [availableWorkers, setAvailableWorkers] = useState<Worker[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // 0 = current month, 1 = next month, 2 = month after
  const [bookingBusinessHours, setBookingBusinessHours] = useState<BusinessHours | undefined>();
  const [bookingAppointmentDuration, setBookingAppointmentDuration] = useState<number | undefined>();
  const dateScrollRef = useRef<ScrollView>(null);
  const widgetServiceAutoAppliedRef = useRef<string | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // UI State
  // Initialize step based on whether business is pre-selected
  // For 30-second booking from dashboard: always start at 'category'
  // For booking from business card: start at 'service' (business already selected)
  const [currentStep, setCurrentStep] = useState<
    'category' | 'service' | 'business' | 'cart' | 'fulfillment' | 'availability' | 'booking'
  >(
    preSelectedBusiness ? 'service' : 'category' // Only skip to service if business is pre-selected
  );
  const [guestAuthMode, setGuestAuthMode] = useState<'new' | 'returning'>('new');
  const [guestPassword, setGuestPassword] = useState('');
  const [showGuestPassword, setShowGuestPassword] = useState(false);
  const [guestAuthError, setGuestAuthError] = useState('');
  const [guestAutoSigningIn, setGuestAutoSigningIn] = useState(false);
  const guestAutoSignInFailedKeyRef = useRef<string | null>(null);
  const guestAutoSignInTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tryAutoGuestSignInRef = useRef<(() => Promise<void>) | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showProductOptionsModal, setShowProductOptionsModal] = useState(false);
  const [selectedProductOptions, setSelectedProductOptions] = useState<Record<string, string>>({}); // optionId -> valueId
  const [productOptionsQuantity, setProductOptionsQuantity] = useState(1); // Quantity for product options modal
  const [customerData, setCustomerData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    address: user?.address || (typeof user?.address === 'object' ? 
      `${user.address.street || ''} ${user.address.city || ''} ${user.address.state || ''} ${user.address.zipcode || ''}`.trim() : 
      '') || '',
  });
  const [showVehiclesModal, setShowVehiclesModal] = useState(false);
  const [vehicles, setVehicles] = useState<VehicleProfile[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId) || null;
  const [automotiveIntake, setAutomotiveIntake] = useState<AutomotiveIntakeData>(emptyAutomotiveIntake());
  const [selectedAutomotiveVehicleId, setSelectedAutomotiveVehicleId] = useState<string | null>(null);
  const guestAccountUserIdRef = useRef<string | null>(null);
  const isAutomotiveBooking =
    isAutomotiveBusinessCategory(selectedCategory) ||
    isAutomotiveBusinessCategory(selectedBusiness?.category) ||
    isAutomotiveBusinessCategory(selectedBusiness?.type);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showCardInput, setShowCardInput] = useState(false);
  
  // Card details state
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [zipCode, setZipCode] = useState('');
  
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'weekly' | 'biweekly' | 'monthly' | 'yearly'>('weekly');
  const [addToCalendar, setAddToCalendar] = useState(true);
  const [connectedCalendars, setConnectedCalendars] = useState<any[]>([]);
  const [selectedCalendar, setSelectedCalendar] = useState<string>('');
  const [isSlotAvailable, setIsSlotAvailable] = useState(true);
  
  // Appointment limit modal state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitCheck, setLimitCheck] = useState<AppointmentLimitCheck | null>(null);

  // Web Stripe checkout state (real Apple Pay / Google Pay / card on the web)
  const [webCheckoutVisible, setWebCheckoutVisible] = useState(false);
  const [webCheckoutInput, setWebCheckoutInput] = useState<CreateAppointmentIntentInput | null>(null);
  const [webCheckoutMethodLabel, setWebCheckoutMethodLabel] = useState<string | undefined>(undefined);
  const webCheckoutResolverRef = useRef<((result: WebStripeCheckoutResult) => void) | null>(null);

  const openWebStripeCheckout = (
    input: CreateAppointmentIntentInput,
    methodLabel?: string
  ): Promise<WebStripeCheckoutResult> => {
    return new Promise((resolve) => {
      webCheckoutResolverRef.current = resolve;
      setWebCheckoutInput(input);
      setWebCheckoutMethodLabel(methodLabel);
      setWebCheckoutVisible(true);
    });
  };

  const finishWebCheckout = (result: WebStripeCheckoutResult) => {
    const resolver = webCheckoutResolverRef.current;
    webCheckoutResolverRef.current = null;
    setWebCheckoutVisible(false);
    setWebCheckoutInput(null);
    setWebCheckoutMethodLabel(undefined);
    if (resolver) resolver(result);
  };

  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'delivery'>('pickup');
  const [merchantDeliveryEnabled, setMerchantDeliveryEnabled] = useState(false);
  const [deliveryQuote, setDeliveryQuote] = useState<DeliveryQuoteResult | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress | null>(null);
  const [deliveryTipCents, setDeliveryTipCents] = useState(300);
  const [deliveryCheckoutVisible, setDeliveryCheckoutVisible] = useState(false);
  const [deliveryCheckoutData, setDeliveryCheckoutData] = useState<
    import('../types/delivery').DeliveryCheckoutIntents | null
  >(null);

  const cartHasProducts = (): boolean => {
    const items =
      selectedItems.length > 0
        ? selectedItems
        : selectedService
          ? [selectedService]
          : [];
    return items.some((i) => !!(i as { isProduct?: boolean }).isProduct);
  };

  const merchantPrepayRequired = !!(
    selectedBusiness?.paymentRequiredAtBooking ?? preSelectedBusiness?.paymentRequiredAtBooking
  );

  const proceedAfterServiceSelection = (skipFulfillment = false) => {
    if (
      !skipFulfillment &&
      DELIVERY_ENABLED &&
      cartHasProducts() &&
      (merchantDeliveryEnabled || merchantPrepayRequired)
    ) {
      setCurrentStep('fulfillment');
      return;
    }
    if (!selectedDate) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setSelectedDate(todayStr);
    }
    setCurrentStep('availability');
  };

  useEffect(() => {
    warmupPaymentApi();
    warmupDeliveryApi();
  }, []);

  useEffect(() => {
    const bizId = selectedBusiness?.id;
    if (!bizId) {
      setMerchantDeliveryEnabled(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${WIDGET_BOOKING_API}/business/${encodeURIComponent(bizId)}`);
        const data = await res.json();
        if (!cancelled && data?.success) {
          setMerchantDeliveryEnabled(data.deliveryEnabled === true);
          setSelectedBusiness((prev: any) => {
            if (!prev || String(prev.id) !== String(bizId)) return prev;
            const nextPaymentRequired =
              data.paymentRequiredAtBooking === true || !!prev.paymentRequiredAtBooking;
            return !!prev.paymentRequiredAtBooking === nextPaymentRequired
              ? prev
              : { ...prev, paymentRequiredAtBooking: nextPaymentRequired };
          });
        }
      } catch {
        if (!cancelled) setMerchantDeliveryEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id]);

  const renderFulfillmentSelection = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Pickup or delivery?</Text>
      <Text style={styles.stepSubtitle}>Choose how you want to receive your order</Text>
      <FulfillmentSelector
        value={fulfillmentType}
        deliveryAvailable={merchantDeliveryEnabled && cartHasProducts()}
        deliveryComingSoon={merchantPrepayRequired}
        onChange={setFulfillmentType}
      />
      <TouchableOpacity
        style={styles.continueButton}
        onPress={() => {
          if (fulfillmentType === 'delivery') {
            setDeliveryQuote(null);
            setDeliveryAddress(null);
            setCurrentStep('booking');
            return;
          }
          proceedAfterServiceSelection(true);
        }}
      >
        <Text style={styles.continueButtonText}>Continue</Text>
      </TouchableOpacity>
    </View>
  );

  const computeCartSubtotalCents = (): number => {
    let items =
      selectedItems.length > 0 ? selectedItems : selectedService ? [selectedService] : [];
    if (selectedService && !items.find((i) => i.id === selectedService.id)) {
      items = [selectedService, ...items];
    }
    const dollars = items.reduce((sum, item) => {
      const qty = getItemQuantity(item.id) || 1;
      return sum + servicePriceForTotal(item.price) * qty;
    }, 0);
    return Math.round(dollars * 100);
  };

  const handleDeliveryPayNow = async () => {
    if (!selectedBusiness?.id || !deliveryQuote?.success || !deliveryAddress) {
      showBookingMessage('Delivery', 'Get a delivery estimate before paying.');
      return;
    }
    const phone = deliveryAddress.phone || customerData.phone;
    if (!phone?.trim()) {
      showBookingMessage('Phone required', 'Enter a phone number for delivery.');
      return;
    }
    setLoading(true);
    try {
      const intents = await createDeliveryCheckoutIntents({
        businessId: selectedBusiness.id,
        subtotalCents: deliveryQuote.subtotalCents,
        deliveryFeeCents: deliveryQuote.deliveryFeeCents,
        tipCents: deliveryTipCents,
        deliveryAddress,
        customerId: userId,
        customerName: customerData.name,
        customerEmail: customerData.email,
        customerPhone: phone.trim(),
        quoteExternalDeliveryId: deliveryQuote.quoteExternalDeliveryId,
        etaMinutes: deliveryQuote.etaMinutes,
      });
      if (!intents.success) {
        showBookingMessage('Checkout', intents.error || 'Could not start checkout.');
        return;
      }
      setDeliveryCheckoutData(intents);
      setDeliveryCheckoutVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeliveryCheckoutResult = async (result: WebStripeDeliveryCheckoutResult) => {
    setDeliveryCheckoutVisible(false);
    setDeliveryCheckoutData(null);
    if (!result.success) {
      if (!result.cancelled) {
        showBookingMessage('Payment failed', result.error || 'Please try again.');
      }
      return;
    }
    showBookingMessage(
      'Order placed',
      `Your delivery order is confirmed.${result.orderId ? ` Order #${result.orderId.slice(-8)}` : ''} You will receive tracking updates when your driver is assigned.`,
    );
    if (onBookingComplete) {
      await onBookingComplete({
        id: result.orderId || 'delivery_order',
        serviceName: 'Delivery order',
        appointmentDate: new Date().toISOString().slice(0, 10),
        appointmentTime: 'ASAP',
        status: 'confirmed',
        customerData,
      } as InstantBooking);
    }
  };

  // Use centralized business categories (same as business owner registration)
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  
  // Filter categories using the centralized search function
  const filteredCategories = categorySearchQuery.trim() 
    ? searchCategories(categorySearchQuery)
    : BUSINESS_CATEGORIES;
  
  // Display filtered results or show limited categories if no search
  const displayedCategories = categorySearchQuery.trim()
    ? filteredCategories
    : (showAllCategories ? BUSINESS_CATEGORIES : BUSINESS_CATEGORIES.slice(0, 8));

  const availablePaymentMethods = [
    { id: 'apple_pay', name: 'Apple Pay' },
    { id: 'google_pay', name: 'Google Pay' },
    { id: 'credit_card', name: 'Credit Card' },
    { id: 'venmo', name: 'Venmo' },
  ];

  useEffect(() => {
    setDefaultDate();
    loadConnectedCalendars();
    loadFavoriteBusinesses();
    loadUserData();

    // Wake the Render payment API early. The free-tier dyno can take ~20-30s
    // to cold-start, which otherwise leaves the Stripe checkout modal stuck on
    // "Preparing secure checkout…" right after the user taps Apple Pay / Card.
    if (Platform.OS === 'web') {
      warmupPaymentApi();
    }

    // Ensure we start at category step if no business is pre-selected (30-second booking from dashboard)
    // This ensures the flow is: Category → Business → Service
    if (!preSelectedBusiness) {
      setCurrentStep('category');
      // Reset all selections when starting fresh from dashboard
      setSelectedCategory('');
      setSelectedBusiness(null);
      setSelectedService(null);
      setSelectedItems([]);
      setServices([]);
      setProducts([]);
      setBusinesses([]);
    }
  }, []);

  // Load saved vehicles (optional) for faster booking
  useEffect(() => {
    const loadVehicles = async () => {
      const accountId = guestMode
        ? isRealBookingAccountId(userId)
          ? userId
          : isRealBookingAccountId(user?.id)
            ? user.id
            : isRealBookingAccountId(user?.uid)
              ? user.uid
              : isRealBookingAccountId(guestAccountUserIdRef.current)
                ? guestAccountUserIdRef.current
                : null
        : userId;
      if (!accountId || !isRealBookingAccountId(accountId)) return;
      try {
        const list = await vehicleGarageService.listVehicles(accountId);
        setVehicles(list);
        // Only auto-pick default on first load; don't override a user selection
        setSelectedVehicleId(prev => {
          if (prev) return prev;
          const def = list.find(v => v.isDefault) || list[0] || null;
          return def?.id || null;
        });
      } catch (e) {
        console.warn('⚠️ OneClickBooking: Failed to load vehicles:', (e as any)?.message);
      }
    };
    loadVehicles();
  }, [userId, guestMode, user?.id, user?.uid]);

  const garageAccountUserId =
    (isRealBookingAccountId(userId) ? userId : null) ||
    (isRealBookingAccountId(user?.id) ? user.id : null) ||
    (isRealBookingAccountId(user?.uid) ? user.uid : null) ||
    (isRealBookingAccountId(guestAccountUserIdRef.current) ? guestAccountUserIdRef.current : null);

  const canUseGarageInBooking = !guestMode || !!garageAccountUserId;

  useEffect(() => {
    if (!isAutomotiveBooking || !canUseGarageInBooking) return;
    if (selectedAutomotiveVehicleId != null) return;
    if (vehicles.length === 0) {
      setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
    }
  }, [
    isAutomotiveBooking,
    canUseGarageInBooking,
    vehicles.length,
    selectedAutomotiveVehicleId,
  ]);

  useEffect(() => {
    guestAutoSignInFailedKeyRef.current = null;
    setGuestAuthError('');
  }, [customerData.email, guestPassword, guestAuthMode]);

  useEffect(() => {
    if (!guestMode || guestAuthMode !== 'returning' || canUseGarageInBooking) {
      return;
    }

    const email = customerData.email.trim();
    const password = guestPassword;
    if (!email || !email.includes('@') || password.length < 6) {
      return;
    }

    if (guestAutoSignInTimerRef.current) {
      clearTimeout(guestAutoSignInTimerRef.current);
    }
    guestAutoSignInTimerRef.current = setTimeout(() => {
      void tryAutoGuestSignInRef.current?.();
    }, 700);

    return () => {
      if (guestAutoSignInTimerRef.current) {
        clearTimeout(guestAutoSignInTimerRef.current);
      }
    };
  }, [
    guestMode,
    guestAuthMode,
    canUseGarageInBooking,
    customerData.email,
    guestPassword,
  ]);

  const showAutomotiveIntakeError = (message: string) => {
    showCenteredAlert('Vehicle details required', message);
  };

  const validateAutomotiveBeforeConfirm = (): boolean => {
    if (!isAutomotiveBooking) return true;
    const err = validateAutomotiveIntake(automotiveIntake);
    if (err) {
      showAutomotiveIntakeError(err);
      return false;
    }
    return true;
  };

  const buildAutomotiveAppointmentExtras = () => {
    if (!isAutomotiveBooking) return {};
    return mapAutomotiveIntakeToAppointmentFields(automotiveIntake);
  };

  const mergeAutomotiveIntoInstructions = (base: string) => {
    if (!isAutomotiveBooking) return base;
    return appendAutomotiveDetailsToInstructions(base, automotiveIntake);
  };

  const handleSelectSavedVehicleForIntake = (vehicle: VehicleProfile) => {
    setSelectedVehicleId(vehicle.id);
    setSelectedAutomotiveVehicleId(vehicle.id);
    setAutomotiveIntake((prev) => ({
      ...prev,
      ...automotiveIntakeFromVehicle(vehicle),
    }));
  };

  const handleChooseManualAutomotiveEntry = () => {
    setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
    setSelectedVehicleId(null);
  };

  const handleAutomotiveIntakeChange = (next: AutomotiveIntakeData) => {
    setAutomotiveIntake(next);
    if (
      !guestMode &&
      selectedAutomotiveVehicleId &&
      selectedAutomotiveVehicleId !== MANUAL_VEHICLE_ENTRY
    ) {
      const vehicle = vehicles.find((v) => v.id === selectedAutomotiveVehicleId);
      if (vehicle && !vehicleMatchesAutomotiveIntake(vehicle, next)) {
        setSelectedAutomotiveVehicleId(MANUAL_VEHICLE_ENTRY);
        setSelectedVehicleId(null);
      }
    }
  };

  const maybeOfferSaveVehicleToGarageAfterGuestBooking = async (
    accountUserId: string | null | undefined,
  ): Promise<void> => {
    if (!guestMode || !isAutomotiveBooking || !accountUserId) return;
    if (validateAutomotiveIntake(automotiveIntake)) return;

    const saveVehicle = async () => {
      const existing = await vehicleGarageService.listVehicles(accountUserId);
      const alreadySaved = existing.some((v) => vehicleMatchesAutomotiveIntake(v, automotiveIntake));
      if (alreadySaved) return;
      await vehicleGarageService.upsertVehicle(
        accountUserId,
        automotiveIntakeToGarageInput(accountUserId, automotiveIntake, {
          isDefault: existing.length === 0,
        }),
      );
    };

    // Widget embed: save silently (no confirm dialog) so the next visit can auto-fill.
    if (isWidgetEmbedContext()) {
      try {
        await saveVehicle();
      } catch (e) {
        console.warn('maybeOfferSaveVehicleToGarageAfterGuestBooking (embed):', e);
      }
      return;
    }

    try {
      const existing = await vehicleGarageService.listVehicles(accountUserId);
      const alreadySaved = existing.some((v) => vehicleMatchesAutomotiveIntake(v, automotiveIntake));
      if (alreadySaved) return;

      const message =
        'Save this vehicle to My Garage for faster booking next time?';

      if (Platform.OS === 'web') {
        if (typeof window !== 'undefined' && window.confirm(message)) {
          try {
            await saveVehicle();
          } catch (e) {
            console.warn('Save vehicle to garage failed:', e);
          }
        }
        return;
      }

      await new Promise<void>((resolve) => {
        Alert.alert('Save vehicle?', message, [
          { text: 'Not now', style: 'cancel', onPress: () => resolve() },
          {
            text: 'Save to My Garage',
            onPress: () => {
              saveVehicle()
                .catch((e) => console.warn('Save vehicle to garage failed:', e))
                .finally(() => resolve());
            },
          },
        ]);
      });
    } catch (e) {
      console.warn('maybeOfferSaveVehicleToGarageAfterGuestBooking:', e);
    }
  };

  const finishSuccessfulBooking = async (
    booking: InstantBooking,
    successMessage: string,
    options?: { accountUserId?: string | null; alertTitle?: string },
  ) => {
    sendGuestBookingConfirmationEmail(booking);

    const widgetEmbed = shouldSuppressWidgetEmbedSuccessAlert();
    const accountUserId =
      options?.accountUserId ?? guestAccountUserIdRef.current ?? booking.customerId;

    try {
      await maybeOfferSaveVehicleToGarageAfterGuestBooking(accountUserId);
    } catch (e) {
      console.warn('finishSuccessfulBooking: garage offer failed', e);
    }

    if (onBookingComplete) {
      try {
        await Promise.resolve(onBookingComplete(booking));
      } catch (error) {
        console.error('finishSuccessfulBooking: onBookingComplete error:', error);
      }
    } else {
      onBack();
    }

    if (widgetEmbed) {
      return;
    }

    const alertTitle = options?.alertTitle || 'Booking Confirmed! 🎉';
    setTimeout(() => {
      showCenteredAlert(alertTitle, successMessage, [
        {
          text: 'View Appointments',
          onPress: () => {
            console.log('✅ User clicked View Appointments - already navigated');
          },
        },
      ]);
    }, 300);
  };

  const rememberGuestAccountForGarage = (accountUserId: string) => {
    if (
      guestMode &&
      accountUserId &&
      accountUserId !== 'guest_app' &&
      accountUserId !== 'guest_embed'
    ) {
      guestAccountUserIdRef.current = accountUserId;
    }
  };

  // Debug: Log payment modal state changes
  useEffect(() => {
    console.log('🔵 Payment Modal State Changed:', {
      showPaymentModal,
      Platform: Platform.OS,
      selectedPaymentMethod,
      availablePaymentMethods: availablePaymentMethods.length
    });
  }, [showPaymentModal, selectedPaymentMethod]);

  // Update customer data when user prop changes
  // But prefer loading from UnifiedAuthService/AuthManager to avoid stale prop data
  useEffect(() => {
    // Only use user prop if loadUserData hasn't loaded data yet
    // This prevents using stale test data from props
    if (user && !customerData.name && !customerData.email) {
      // Extract phone from user object
      let phone = '';
      if (user.phone) {
        phone = user.phone;
      } else if (typeof user.address === 'object' && user.address?.phone) {
        phone = user.address.phone;
      }
      
      setCustomerData(prev => ({
        ...prev,
        name: user.name || '',
        phone: phone,
        email: user.email || user.username || '',
      }));
      console.log('✅ OneClickBooking: Pre-filled customer data from user prop:', {
        name: user.name,
        phone: phone,
        email: user.email || user.username
      });
    }
  }, [user]);

  // Load services when category is selected (but skip if we have a pre-selected business)
  useEffect(() => {
    if (selectedCategory && !preSelectedBusiness) {
      loadBusinessesForCategory(selectedCategory);
    }
  }, [selectedCategory, preSelectedBusiness]);

  // Load services when business is selected
  useEffect(() => {
    if (selectedBusiness) {
      loadServicesForBusiness(selectedBusiness);
    }
  }, [selectedBusiness]);

  // Load business hours from Firestore when a BookAPT business is selected
  useEffect(() => {
    const businessId = selectedBusiness?.ownerId || selectedBusiness?.id;
    if (!selectedBusiness?.isOnBookAPT || !businessId) {
      setBookingBusinessHours(undefined);
      setBookingAppointmentDuration(undefined);
      return;
    }

    let cancelled = false;
    (async () => {
      const fromFirestore = await fetchBusinessBookingSettings(businessId);
      if (cancelled) return;

      if (fromFirestore.businessHours) {
        setBookingBusinessHours(fromFirestore.businessHours);
        setBookingAppointmentDuration(fromFirestore.appointmentDuration);
      }

      if (fromFirestore.paymentRequiredAtBooking === true) {
        setSelectedBusiness((prev: any) =>
          prev && prev.paymentRequiredAtBooking !== true
            ? { ...prev, paymentRequiredAtBooking: true }
            : prev,
        );
      }

      if (fromFirestore.businessHours) {
        return;
      }

      try {
        const businessOwner = await AuthManager.getSavedBusinessOwner();
        if (
          businessOwner &&
          (businessOwner.id === selectedBusiness.id || businessOwner.id === selectedBusiness.ownerId)
        ) {
          setBookingBusinessHours(businessOwner.businessHours);
          setBookingAppointmentDuration(businessOwner.appointmentDuration);
          setSelectedBusiness((prev: any) =>
            prev && !!prev.paymentRequiredAtBooking !== !!businessOwner.paymentRequiredAtBooking
              ? {
                  ...prev,
                  paymentRequiredAtBooking: !!businessOwner.paymentRequiredAtBooking,
                }
              : prev,
          );
          return;
        }
      } catch {
        /* ignore */
      }

      setBookingBusinessHours(undefined);
      setBookingAppointmentDuration(undefined);
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, selectedBusiness?.ownerId, selectedBusiness?.isOnBookAPT]);

  useEffect(() => {
    if (merchantPrepayRequired && fulfillmentType === 'delivery') {
      setFulfillmentType('pickup');
    }
  }, [merchantPrepayRequired, fulfillmentType]);

  useEffect(() => {
    if (selectedDate && bookingBusinessHours && !isBusinessOpenOnDate(bookingBusinessHours, selectedDate)) {
      setSelectedDate('');
      setSelectedTime('');
      setAvailableSlots([]);
    }
  }, [bookingBusinessHours, selectedDate]);

  useEffect(() => {
    if (selectedService && selectedDate && selectedTime) {
      checkSlotAvailability();
    }
  }, [selectedService, selectedDate, selectedTime]);

  // Widget / guest flow: auto-load time slots when landing on availability with a date already set
  useEffect(() => {
    if (currentStep !== 'availability' || !selectedBusiness?.id || !selectedDate) {
      return;
    }
    loadAvailableSlotsWithWorkers(selectedBusiness.id, selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedBusiness?.id, selectedDate]);

  // Seed business hours from widget public API payload (avoids Firestore in guest embed)
  useEffect(() => {
    if (!preSelectedBusiness?.businessHours) {
      return;
    }
    setBookingBusinessHours(preSelectedBusiness.businessHours);
    if (preSelectedBusiness.appointmentDuration != null) {
      setBookingAppointmentDuration(preSelectedBusiness.appointmentDuration);
    }
  }, [preSelectedBusiness?.id, preSelectedBusiness?.businessHours]);

  useEffect(() => {
    if (!preSelectedBusiness?.paymentRequiredAtBooking) {
      return;
    }
    setSelectedBusiness((prev: any) =>
      prev ? { ...prev, paymentRequiredAtBooking: true } : preSelectedBusiness,
    );
  }, [preSelectedBusiness?.id, preSelectedBusiness?.paymentRequiredAtBooking]);

  // Handle pre-selected business from favorites
  useEffect(() => {
    console.log('🔍 OneClickBookingScreen preSelectedBusiness check:', preSelectedBusiness);
    if (preSelectedBusiness) {
      console.log('🎯 Pre-selected business detected:', preSelectedBusiness);
      console.log('🎯 Business name:', preSelectedBusiness.name);
      console.log('🎯 Business category:', preSelectedBusiness.category);
      console.log('🎯 Business type:', preSelectedBusiness.type);
      
      // Set the business category/type (but this won't trigger loadBusinessesForCategory due to the check above)
      const businessCategory = preSelectedBusiness.category || preSelectedBusiness.type || '';
      setSelectedCategory(businessCategory);
      
      // Set the selected business directly
      setSelectedBusiness(preSelectedBusiness);
      
      // Skip to service selection step
      setCurrentStep('service');
      
      // Load services for this business
      loadServicesForBusiness(preSelectedBusiness);
    } else {
      console.log('❌ No pre-selected business detected');
    }
  }, [preSelectedBusiness?.id]);

  const loadServices = async () => {
    try {
      console.log('Loading services...');
      setLoading(true);
      const popularServices = await instantBookingService.getPopularServices();
      console.log('Loaded services:', popularServices);
      setServices(popularServices);
    } catch (error) {
      console.error('Error loading services:', error);
      // Set some default services if loading fails
      const defaultServices = [
        {
          id: 'haircut_basic',
          serviceName: 'Basic Haircut',
          duration: 30,
          price: 45,
          description: 'Professional haircut and styling',
          icon: '💇‍♀️',
          category: 'hair',
          isPopular: true,
          requirements: ['Clean hair', 'No styling products'],
          availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']
        },
        {
          id: 'facial_basic',
          serviceName: 'Basic Facial',
          duration: 60,
          price: 80,
          description: 'Standard facial treatment',
          icon: '✨',
          category: 'beauty',
          isPopular: true,
          requirements: ['Clean face', 'No makeup', 'Arrive 10 minutes early'],
          availableSlots: ['10:00', '11:00', '14:00', '15:00', '16:00']
        }
      ];
      setServices(defaultServices);
    } finally {
      setLoading(false);
    }
  };

  const setDefaultDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  };

  const loadConnectedCalendars = async () => {
    try {
      const calendars = await calendarService.getConnectedAccounts();
      setConnectedCalendars(calendars);
      if (calendars.length > 0) {
        setSelectedCalendar(calendars[0].id);
      }
    } catch (error) {
      console.error('Error loading connected calendars:', error);
    }
  };

  // New step-by-step functions
  const loadFavoriteBusinesses = async () => {
    try {
      // Load user's favorite businesses
      const favorites = await favoritesService.getFavorites();
      setFavoriteBusinesses(favorites);
    } catch (error) {
      console.error('Error loading favorite businesses:', error);
    }
  };

  const loadUserData = async () => {
    try {
      // Load logged-in user data and pre-fill contact information
      // Use UnifiedAuthService for web (Firebase) and AuthManager for mobile
      let userFromAuth: any = null;
      
      if (Platform.OS === 'web') {
        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
        userFromAuth = await UnifiedAuthService.getCurrentUser();
      } else {
        userFromAuth = await AuthManager.getSavedUser();
      }
      
      // Prefer auth source, then fall back to passed-in user prop.
      const resolvedUser = userFromAuth || user;

      if (resolvedUser) {
        // Extract phone from user object (could be in different formats)
        let phone = '';
        if (resolvedUser.phone) {
          phone = resolvedUser.phone;
        } else if (typeof resolvedUser.address === 'object' && resolvedUser.address?.phone) {
          phone = resolvedUser.address.phone;
        }
        
        setCustomerData(prev => ({
          ...prev,
          // Keep any already-entered value if the fetched profile field is missing.
          name: resolvedUser.name || prev.name || '',
          phone: phone || prev.phone || '',
          email: resolvedUser.email || resolvedUser.username || prev.email || '',
          address:
            (typeof resolvedUser.address === 'string' && resolvedUser.address) ||
            (typeof resolvedUser.address === 'object'
              ? `${resolvedUser.address.street || ''} ${resolvedUser.address.city || ''} ${resolvedUser.address.state || ''} ${resolvedUser.address.zipcode || ''}`.trim()
              : '') ||
            prev.address ||
            '',
        }));
        console.log('✅ OneClickBooking: Pre-filled customer data for logged-in user:', {
          name: resolvedUser.name,
          phone: phone,
          email: resolvedUser.email || resolvedUser.username
        });
      } else {
        console.log('⚠️ OneClickBooking: No user data found from auth; keeping existing contact values');
      }
    } catch (error) {
      console.error('❌ OneClickBooking: Error loading user data:', error);
      // Do not clear fields on transient auth/network errors.
      if (user) {
        setCustomerData(prev => ({
          ...prev,
          name: prev.name || user.name || '',
          phone: prev.phone || user.phone || (typeof user.address === 'object' ? user.address?.phone || '' : ''),
          email: prev.email || user.email || user.username || '',
        }));
      }
    }
  };

  const normalizeContactValue = (value: any): string => {
    if (typeof value !== 'string') return '';
    return value.trim();
  };

  const resolveCustomerContactData = async () => {
    const merged = {
      ...customerData,
      name: normalizeContactValue(customerData.name),
      phone: normalizeContactValue(customerData.phone),
      email: normalizeContactValue(customerData.email),
    };

    if (merged.name && merged.phone && merged.email) {
      return merged;
    }

    try {
      let currentUser: any = null;
      if (Platform.OS === 'web') {
        const { UnifiedAuthService } = await import('../services/unifiedAuthService');
        currentUser = await UnifiedAuthService.getCurrentUser();
      } else {
        currentUser = await AuthManager.getSavedUser();
      }

      const fallbackName = normalizeContactValue(currentUser?.name || user?.name || '');
      const fallbackPhone = normalizeContactValue(
        currentUser?.phone ||
        currentUser?.address?.phone ||
        user?.phone ||
        user?.address?.phone ||
        ''
      );
      const fallbackEmail = normalizeContactValue(
        currentUser?.email ||
        currentUser?.username ||
        user?.email ||
        user?.username ||
        ''
      );

      const resolved = {
        ...merged,
        name: merged.name || fallbackName,
        phone: merged.phone || fallbackPhone,
        email: merged.email || fallbackEmail,
      };

      setCustomerData(prev => ({
        ...prev,
        name: resolved.name,
        phone: resolved.phone,
        email: resolved.email,
      }));

      return resolved;
    } catch (error) {
      console.warn('⚠️ Could not resolve customer contact data:', error);
      return merged;
    }
  };

  const resolveBookingZipcode = async (): Promise<string> => {
    const fallback = '76010';
    try {
      const { UnifiedAuthService } = await import('../services/unifiedAuthService');
      const currentUser = await UnifiedAuthService.getCurrentUser();
      if (currentUser?.address?.zipcode) {
        return currentUser.address.zipcode;
      }
    } catch (error) {
      console.error('Error getting user zipcode:', error);
    }
    if (guestMode) {
      const guestZip = getGuestZipcode() || (await getGuestZipcodeAsync());
      if (guestZip) {
        return guestZip;
      }
    }
    return fallback;
  };

  const loadBusinessesForCategory = async (categoryId: string) => {
    setLoading(true);
    try {
      const userZipcode = await resolveBookingZipcode();
      
      // Search for businesses in this category
      // Don't use category name as query - let category filter handle it
      // Using category name as query can match wrong businesses (e.g., "Automotive Services" might match "Carpet Cleaning Services")
      const searchResults = await hybridBusinessSearchService.search({
        query: '', // Empty query - let category filter do the work
        zipcode: userZipcode,
        category: categoryId, // Use 'category' not 'categoryId' to match service interface
      });
      
      // Filter only BookAPT businesses and validate they match the category
      const selectedCategory = categories.find(cat => cat.id === categoryId);
      const categoryKeywords = selectedCategory?.keywords || [];
      
      const bookaptBusinesses = searchResults.businesses.filter(biz => {
        // Must be on BookAPT
        if (!biz.isOnBookAPT) return false;
        
        // Validate category match using keywords
        if (categoryKeywords.length > 0) {
          const businessCategory = (biz.category || '').toLowerCase();
          const businessName = (biz.name || '').toLowerCase();
          const businessText = `${businessCategory} ${businessName}`;
          
          // Check if business matches any category keyword
          const matchesCategory = categoryKeywords.some(keyword => 
            businessText.includes(keyword.toLowerCase())
          );
          
          if (!matchesCategory) {
            console.log(`⚠️ Filtered out "${biz.name}" - doesn't match ${categoryId} category keywords`);
            return false;
          }
        }
        
        return true;
      });
      
      if (bookaptBusinesses.length > 0) {
        const formattedBusinesses = bookaptBusinesses.map(biz => ({
          id: biz.id,
          name: biz.name,
          category: biz.category,
          rating: biz.rating || 4.0,
          distance: biz.distance ? `${(biz.distance / 1609).toFixed(1)} miles` : '0.5 miles',
          address: `${biz.address}, ${biz.city}, ${biz.state} ${biz.zipcode}`,
          phone: biz.phone || 'N/A',
          isFavorite: false,
          isOnBookAPT: true,
          source: biz.source,
          ownerId: biz.ownerId,
          paymentRequiredAtBooking: !!biz.paymentRequiredAtBooking,
          services: [],
          priceRange: '$$',
          hours: '9:00 AM - 6:00 PM',
        }));
        
        setBusinesses(formattedBusinesses);
        setCurrentStep('business');
      } else {
        // No BookAPT businesses found - navigate to business exploration (silently)
        console.log('⚠️ No BookAPT businesses found for this category - redirecting to business exploration');
        if (onNavigateToBusinessExploration) {
          // Store the selected category so BusinessExplorationScreen can use it
          if (Platform.OS === 'web' && typeof window !== 'undefined' && selectedCategory) {
            sessionStorage.setItem('preselectedCategory', selectedCategory);
            console.log('🔍 Stored preselected category:', selectedCategory);
          }
          onNavigateToBusinessExploration();
        } else {
          onBack();
        }
      }
    } catch (error) {
      console.error('Error loading businesses for category:', error);
      // On error, navigate to business exploration
      if (onNavigateToBusinessExploration) {
        // Store the selected category so BusinessExplorationScreen can use it
        if (Platform.OS === 'web' && typeof window !== 'undefined' && selectedCategory) {
          sessionStorage.setItem('preselectedCategory', selectedCategory);
          console.log('🔍 Stored preselected category:', selectedCategory);
        }
        onNavigateToBusinessExploration();
      } else {
        onBack();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessesForService = async (serviceId: string) => {
    setLoading(true);
    try {
      // Get service name from selected service
      const serviceName = (selectedService as any)?.name || serviceId;
      
      console.log('🔍 Searching for businesses offering:', serviceName);
      
      const userZipcode = await resolveBookingZipcode();
      console.log('📍 Using zipcode for business search:', userZipcode);
      
      // Use hybrid search to find businesses (BookAPT + Foursquare)
      const searchResults = await hybridBusinessSearchService.search({
        query: serviceName,
        zipcode: userZipcode, // Use real user zipcode
      });
      
      console.log(`✅ Found ${searchResults.totalFound} businesses (${searchResults.source})`);
      
      if (searchResults.totalFound > 0) {
        // Filter only BookAPT businesses
        const bookaptBusinesses = searchResults.businesses.filter(biz => biz.isOnBookAPT === true);
        
        if (bookaptBusinesses.length > 0) {
          // Convert external businesses to our format
          const formattedBusinesses = bookaptBusinesses.map(biz => ({
            id: biz.id,
            name: biz.name,
            category: biz.category,
            rating: biz.rating || 4.0,
            distance: biz.distance ? `${(biz.distance / 1609).toFixed(1)} miles` : '0.5 miles',
            address: `${biz.address}, ${biz.city}, ${biz.state} ${biz.zipcode}`,
            phone: biz.phone || 'N/A',
            isFavorite: false,
            isOnBookAPT: true,
            source: biz.source,
            ownerId: biz.ownerId,
            paymentRequiredAtBooking: !!biz.paymentRequiredAtBooking,
            services: [serviceName],
            priceRange: '$$',
            hours: '9:00 AM - 6:00 PM',
            availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']
          }));
          
          setBusinesses(formattedBusinesses);
        } else {
          // No BookAPT businesses found - navigate to business exploration (silently)
          console.log('⚠️ No BookAPT businesses found for this service - redirecting to business exploration');
          if (onNavigateToBusinessExploration) {
            // Store the selected category so BusinessExplorationScreen can use it
            if (Platform.OS === 'web' && typeof window !== 'undefined' && selectedCategory) {
              sessionStorage.setItem('preselectedCategory', selectedCategory);
              console.log('🔍 Stored preselected category:', selectedCategory);
            }
            onNavigateToBusinessExploration();
          } else {
            onBack();
          }
          return;
        }
      } else {
        // No results at all - navigate to business exploration (silently)
        console.log('⚠️ No businesses found for this service - redirecting to business exploration');
        if (onNavigateToBusinessExploration) {
          // Store the selected category so BusinessExplorationScreen can use it
          if (Platform.OS === 'web' && typeof window !== 'undefined' && selectedCategory) {
            sessionStorage.setItem('preselectedCategory', selectedCategory);
            console.log('🔍 Stored preselected category:', selectedCategory);
          }
          onNavigateToBusinessExploration();
        } else {
          onBack();
        }
        return;
      }
      
      setCurrentStep('business');
    } catch (error) {
      console.error('Error loading businesses for service:', error);
      // On error, navigate to business exploration (silently)
      if (onNavigateToBusinessExploration) {
        // Store the selected category so BusinessExplorationScreen can use it
        if (Platform.OS === 'web' && typeof window !== 'undefined' && selectedCategory) {
          sessionStorage.setItem('preselectedCategory', selectedCategory);
          console.log('🔍 Stored preselected category:', selectedCategory);
        }
        onNavigateToBusinessExploration();
      } else {
        onBack();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogFromWidgetPublicApi = async (
    businessId: string,
    business: any,
  ): Promise<{ services: QuickBookOption[]; products: QuickBookOption[] } | null> => {
    try {
      const res = await fetch(
        `${WIDGET_BOOKING_API}/business/${encodeURIComponent(businessId)}`,
      );
      const data = await res.json();
      if (!res.ok || !data?.success || !Array.isArray(data.services)) {
        return null;
      }

      if (data.paymentRequiredAtBooking === true) {
        setSelectedBusiness((prev: any) =>
          prev && prev.paymentRequiredAtBooking !== true
            ? { ...prev, paymentRequiredAtBooking: true }
            : prev,
        );
      }

      const mappedServices: QuickBookOption[] = [];
      const mappedProducts: QuickBookOption[] = [];

      for (const row of data.services) {
        const id = String(row.id || '');
        const isProduct = id.startsWith('product_');
        const item: QuickBookOption = {
          id,
          serviceName: row.name || (isProduct ? 'Product' : 'Service'),
          duration: Number(row.duration) || 30,
          price: isProduct ? (Number(row.price) || 0) : normalizeStoredServicePrice(row.price),
          description: isProduct ? String(row.description || '').trim() : '',
          icon: isProduct ? '📦' : '⚙️',
          category: row.category || business.category || business.type || '',
          isPopular: !!row.featuredOnWidget,
          availableSlots: [],
          requirements: [],
          isProduct,
          ...(isProduct
            ? {
                unit: row.unit || '',
                stockQuantity: row.stockQuantity,
                isInStock: row.isInStock !== false,
                images: Array.isArray(row.images)
                  ? row.images.filter((url: unknown) => typeof url === 'string' && url.trim())
                  : row.image
                    ? [String(row.image)]
                    : undefined,
                imageThumbnails: Array.isArray(row.imageThumbnails)
                  ? row.imageThumbnails.filter((url: unknown) => typeof url === 'string' && url.trim())
                  : undefined,
                options:
                  Array.isArray(row.options) && row.options.length > 0
                    ? row.options
                    : undefined,
              }
            : {}),
        };
        if (isProduct) {
          mappedProducts.push(item);
        } else {
          mappedServices.push(item);
        }
      }

      return { services: mappedServices, products: mappedProducts };
    } catch (error) {
      console.warn('⚠️ Widget public catalog load failed:', error);
      return null;
    }
  };

  const notifyGuestAuthIssue = (message: string) => {
    setGuestAuthError(message);
    showCenteredAlert(
      guestAuthMode === 'returning' ? 'Sign in required' : 'Your details needed',
      message,
    );
  };

  const validateGuestContactBeforeConfirm = (): boolean => {
    if (!guestMode || (user && (user.id || (user as any).uid))) {
      return true;
    }

    const email = customerData.email.trim();
    const name = customerData.name.trim();
    const phone = customerData.phone.trim();

    if (guestAuthMode === 'returning') {
      if (!email) {
        notifyGuestAuthIssue('Please enter your email.');
        return false;
      }
      if (!guestPassword || guestPassword.length < 6) {
        notifyGuestAuthIssue('Enter a password with at least 6 characters.');
        return false;
      }
    } else {
      if (!name || !phone) {
        notifyGuestAuthIssue('Please enter your name and phone.');
        return false;
      }
      if (!email) {
        notifyGuestAuthIssue('Please enter your email.');
        return false;
      }
      if (!guestPassword || guestPassword.length < 6) {
        notifyGuestAuthIssue('Enter a password with at least 6 characters.');
        return false;
      }
    }

    setGuestAuthError('');
    return true;
  };

  const ensureGuestAuthenticated = async (
    contact: { name: string; phone: string; email: string },
    password: string,
    options?: { silent?: boolean },
  ): Promise<{ userId: string; user: any } | null> => {
    if (!guestMode) {
      return null;
    }

    const email = contact.email.trim().toLowerCase();
    const name = contact.name.trim();
    const phone = contact.phone.trim();

    if (!email) {
      if (!options?.silent) {
        notifyGuestAuthIssue('Please enter your email.');
      }
      return null;
    }
    if (guestAuthMode === 'new' && (!name || !phone)) {
      if (!options?.silent) {
        notifyGuestAuthIssue('Please enter your name and phone.');
      }
      return null;
    }
    if (!password || password.length < 6) {
      if (!options?.silent) {
        notifyGuestAuthIssue('Enter a password with at least 6 characters.');
      }
      return null;
    }

    setGuestAuthError('');
    if (!options?.silent) {
      setLoading(true);
    }
    try {
      const { UnifiedAuthService } = await import('../services/unifiedAuthService');

      if (guestAuthMode === 'returning') {
        try {
          const loggedIn = await UnifiedAuthService.loginUser(email, password);
          onGuestAuthenticated?.(loggedIn);
          return { userId: loggedIn.id || (loggedIn as any).uid, user: loggedIn };
        } catch {
          notifyGuestAuthIssue('Could not sign in. Check your email and password.');
          return null;
        }
      }

      try {
        const guestZip = getGuestZipcode() || (await getGuestZipcodeAsync());
        const guestCountry = getGuestCountry();
        const registered = await UnifiedAuthService.registerUser(email, password, {
          name,
          phone,
          userType: 'customer',
          ...(guestZip
            ? {
                address: {
                  zipcode: guestZip,
                  country: guestCountry,
                },
              }
            : {}),
        });
        onGuestAuthenticated?.(registered);
        return { userId: registered.id || (registered as any).uid, user: registered };
      } catch (registerError: any) {
        const msg = String(registerError?.message || registerError || '').toLowerCase();
        if (
          msg.includes('already') ||
          msg.includes('email-already-in-use') ||
          msg.includes('already registered')
        ) {
          notifyGuestAuthIssue(
            'This email is already registered. Switch to "Returning customer" and sign in.',
          );
          return null;
        }
        throw registerError;
      }
    } catch (error: any) {
      console.error('Guest widget auth failed:', error);
      notifyGuestAuthIssue(error?.message || 'Could not create your account. Try again.');
      return null;
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  tryAutoGuestSignInRef.current = async () => {
    if (!guestMode || guestAuthMode !== 'returning' || canUseGarageInBooking) {
      return;
    }

    const email = customerData.email.trim().toLowerCase();
    const password = guestPassword;
    if (!email || !email.includes('@') || password.length < 6) {
      return;
    }

    const attemptKey = `${email}:${password}`;
    if (guestAutoSignInFailedKeyRef.current === attemptKey) {
      return;
    }

    setGuestAutoSigningIn(true);
    try {
      const auth = await ensureGuestAuthenticated(
        {
          name: customerData.name.trim() || 'Customer',
          phone: customerData.phone.trim() || '',
          email,
        },
        password,
        { silent: true },
      );
      if (auth) {
        rememberGuestAccountForGarage(auth.userId);
        guestAutoSignInFailedKeyRef.current = null;
        return;
      }
      guestAutoSignInFailedKeyRef.current = attemptKey;
    } finally {
      setGuestAutoSigningIn(false);
    }
  };

  const renderGuestContactSection = () => (
    <View style={styles.contactInfoSection}>
      <Text style={styles.contactInfoTitle}>
        {guestAuthMode === 'new' ? 'Your details to confirm' : 'Sign in to continue'}
      </Text>
      <View style={styles.guestAuthToggleRow}>
        <TouchableOpacity
          style={[
            styles.guestAuthToggleBtn,
            guestAuthMode === 'new' && styles.guestAuthToggleBtnActive,
          ]}
          onPress={() => setGuestAuthMode('new')}
        >
          <Text
            style={[
              styles.guestAuthToggleText,
              guestAuthMode === 'new' && styles.guestAuthToggleTextActive,
            ]}
          >
            New customer
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.guestAuthToggleBtn,
            guestAuthMode === 'returning' && styles.guestAuthToggleBtnActive,
          ]}
          onPress={() => setGuestAuthMode('returning')}
        >
          <Text
            style={[
              styles.guestAuthToggleText,
              guestAuthMode === 'returning' && styles.guestAuthToggleTextActive,
            ]}
          >
            Returning
          </Text>
        </TouchableOpacity>
      </View>
      {guestAuthMode === 'new' ? (
        <>
          <AppTextInput
            style={styles.guestInput}
            placeholder="Full name"
            value={customerData.name}
            onChangeText={(text) => setCustomerData((prev) => ({ ...prev, name: text }))}
            autoComplete="name"
          />
          <AppTextInput
            style={styles.guestInput}
            placeholder="Phone"
            value={customerData.phone}
            onChangeText={(text) => setCustomerData((prev) => ({ ...prev, phone: text }))}
            keyboardType="phone-pad"
            autoComplete="tel"
          />
        </>
      ) : null}
      <AppTextInput
        style={styles.guestInput}
        placeholder="Email"
        value={customerData.email}
        onChangeText={(text) => setCustomerData((prev) => ({ ...prev, email: text }))}
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
      />
      <View style={styles.guestPasswordRow}>
        <AppTextInput
          style={styles.guestPasswordInput}
          placeholder={guestAuthMode === 'new' ? 'Create password (6+ characters)' : 'Password'}
          value={guestPassword}
          onChangeText={setGuestPassword}
          secureTextEntry={!showGuestPassword}
          autoComplete={guestAuthMode === 'new' ? 'new-password' : 'current-password'}
          onEndEditing={() => {
            if (guestAuthMode === 'returning') {
              void tryAutoGuestSignInRef.current?.();
            }
          }}
        />
        <TouchableOpacity
          style={styles.guestPasswordEyeBtn}
          onPress={() => setShowGuestPassword(!showGuestPassword)}
          accessibilityRole="button"
          accessibilityLabel={showGuestPassword ? 'Hide password' : 'Show password'}
        >
          <Text style={styles.guestPasswordEyeIcon}>{showGuestPassword ? '👁️' : '👁️‍🗨️'}</Text>
        </TouchableOpacity>
      </View>
      {guestAuthError ? (
        <Text style={styles.guestAuthError}>{guestAuthError}</Text>
      ) : guestAutoSigningIn ? (
        <Text style={styles.autoFilledText}>Signing in…</Text>
      ) : guestAuthMode === 'returning' && canUseGarageInBooking ? (
        <Text style={styles.autoFilledText}>
          ✓ Signed in — your saved vehicles will appear below.
        </Text>
      ) : (
        <Text style={styles.autoFilledText}>
          {guestAuthMode === 'new'
            ? 'We will create your BookAPT account when you confirm.'
            : 'Enter your password — we sign you in automatically to load saved vehicles.'}
        </Text>
      )}
    </View>
  );

  const loadServicesForBusiness = async (business: any) => {
    setLoading(true);
    try {
      console.log('🎯 Loading services for pre-selected business:', business.name);
      console.log('🎯 Business object:', JSON.stringify(business, null, 2));
      
      // Try to load actual services from Firestore (same pattern as ServiceManagementScreen)
      let businessServices: QuickBookOption[] = [];
      // Services are saved under businesses/{businessOwnerId}/services/all.
      // Favorites may lose ownerId and only keep listing ids, so we try multiple id candidates.
      const candidateBusinessIds = Array.from(
        new Set(
          [
            business.ownerId,
            business.businessOwnerId,
            business.businessId,
            business.id,
            business.docId,
            business.firestoreId,
            business.source === 'bookapt' ? business.id : null,
          ].filter(Boolean)
        )
      ) as string[];
      const primaryBusinessId = candidateBusinessIds[0] || null;
      const fallbackBusinessId = candidateBusinessIds[1] || null;
      
      console.log('🎯 Primary Business ID for services:', primaryBusinessId);
      console.log('🎯 Fallback Business ID:', fallbackBusinessId);
      console.log('🎯 Business object keys:', Object.keys(business));
      console.log('🎯 Business.ownerId:', business.ownerId);
      console.log('🎯 Business.id:', business.id);
      console.log('🎯 Platform.OS:', Platform.OS);

      if (guestMode && Platform.OS === 'web') {
        const catalogIds = Array.from(
          new Set(
            [
              business.ownerId,
              business.businessOwnerId,
              business.businessId,
              business.id,
            ].filter(Boolean),
          ),
        ) as string[];

        for (const bizId of catalogIds) {
          const catalog = await loadCatalogFromWidgetPublicApi(bizId, business);
          if (catalog && (catalog.services.length > 0 || catalog.products.length > 0)) {
            setServices(catalog.services);
            setProducts(catalog.products);
            console.log(
              '✅ Guest widget catalog loaded:',
              catalog.services.length,
              'services,',
              catalog.products.length,
              'products',
            );
            setLoading(false);
            return;
          }
        }
      }
      
      // Try loading services with primary business ID first
      const tryLoadServices = async (businessId: string): Promise<boolean> => {
        if (!businessId) {
          return false;
        }

        try {
          let actualServices: any[] = [];

          if (Platform.OS === 'web') {
            // Use require to ensure same module instance as unifiedAuthService
            const { doc, getDoc } = require('firebase/firestore');
            const unifiedAuthService = require('../services/unifiedAuthService');
            const db = unifiedAuthService.getWebDb();

            console.log('🎯 Firestore DB available (web):', !!db);
            if (!db) return false;

            const servicesPath = `businesses/${businessId}/services/all`;
            console.log('🎯 Loading services from path:', servicesPath);
            const servicesDocRef = doc(db, 'businesses', businessId, 'services', 'all');
            const servicesDoc = await getDoc(servicesDocRef);
            console.log('🎯 Services document exists:', servicesDoc.exists());
            if (!servicesDoc.exists()) {
              console.log('⚠️ Services document does not exist at path:', servicesPath);
              return false;
            }

            const data = servicesDoc.data();
            console.log('🎯 Services document data keys:', Object.keys(data));
            actualServices = data.services || [];
          } else {
            const { getMobileFirestore, initializeFirebase } = require('../services/unifiedAuthService');
            initializeFirebase();
            const getFirestore = getMobileFirestore?.();
            if (!getFirestore || typeof getFirestore !== 'function') return false;
            const db = getFirestore();
            if (!db || typeof db.collection !== 'function') return false;

            const servicesPath = `businesses/${businessId}/services/all`;
            console.log('🎯 Loading services from Firestore (mobile):', servicesPath);
            const servicesDoc = await db
              .collection('businesses')
              .doc(businessId)
              .collection('services')
              .doc('all')
              .get();
            if (!servicesDoc.exists) {
              console.log('⚠️ Services document does not exist at path:', servicesPath);
              return false;
            }
            const data = servicesDoc.data() || {};
            actualServices = data.services || [];
          }

          console.log('🎯 Raw services array length:', actualServices.length);
          if (actualServices.length > 0) {
            console.log('🎯 Sample service:', JSON.stringify(actualServices[0], null, 2));

            // Convert service format to QuickBookOption format (only active services)
            businessServices = actualServices
              .filter((svc: any) => svc.isActive !== false)
              .map((svc: any) => ({
                id: svc.id || `service_${svc.name}`,
                serviceName: svc.name || 'Service',
                duration: svc.duration || 60,
                price: normalizeStoredServicePrice(svc.price),
                description: svc.description || '',
                icon: svc.icon || '⚙️',
                category: business.category || business.type || '',
                isPopular: svc.isPopular || false,
                availableSlots: [],
                requirements: svc.requirements || [],
              }));
            console.log('✅ Loaded actual services from business Firestore:', businessServices.length);
            console.log('✅ Service names:', businessServices.map(s => s.serviceName));
            return true; // Successfully loaded
          }

          console.log('⚠️ Services array is empty in Firestore document');
        } catch (error) {
          console.error('❌ Error loading services from Firestore:', error);
        }
        return false; // Failed to load
      };
      
      // Try all known IDs first
      if (candidateBusinessIds.length > 0) {
        for (const tryBusinessId of candidateBusinessIds) {
          const loaded = await tryLoadServices(tryBusinessId);
          if (loaded) break;
        }
      } else {
        console.log('⚠️ Skipping Firestore load - no businessId available, Platform:', Platform.OS);
      }

      // Name-based fallback: favorite cards may have stale/wrong IDs.
      // If no services yet, resolve BookAPT business doc id by exact name and retry.
      if (businessServices.length === 0 && business?.name) {
        try {
          const normalizedName = String(business.name).trim();
          const normalizedNameLower = normalizedName.toLowerCase();
          console.log('🔎 Attempting name-based business id resolution for services:', normalizedName);

          if (Platform.OS === 'web') {
            const { collection, getDocs, query, where } = require('firebase/firestore');
            const unifiedAuthService = require('../services/unifiedAuthService');
            const db = unifiedAuthService.getWebDb();
            if (db) {
              const q = query(collection(db, 'businesses'), where('name', '==', normalizedName));
              const snap = await getDocs(q);
              for (const docSnap of snap.docs) {
                const resolvedId = docSnap.id;
                if (!candidateBusinessIds.includes(resolvedId)) {
                  candidateBusinessIds.push(resolvedId);
                }
              }
              // Always do a case-insensitive fallback scan.
              // Exact query can miss results when stored names vary by casing/spacing.
              const allSnap = await getDocs(collection(db, 'businesses'));
              allSnap.forEach((docSnap: any) => {
                const data = docSnap.data?.() || {};
                const name = String(data.name || '').trim().toLowerCase();
                if (name && name === normalizedNameLower) {
                  const resolvedId = docSnap.id;
                  if (!candidateBusinessIds.includes(resolvedId)) {
                    candidateBusinessIds.push(resolvedId);
                  }
                }
              });
            }
          } else {
            const { getMobileFirestore, initializeFirebase } = require('../services/unifiedAuthService');
            initializeFirebase();
            const getFirestore = getMobileFirestore?.();
            if (getFirestore && typeof getFirestore === 'function') {
              const db = getFirestore();
              if (db && typeof db.collection === 'function') {
                const snap = await db
                  .collection('businesses')
                  .where('name', '==', normalizedName)
                  .get();
                snap.forEach((docSnap: any) => {
                  const resolvedId = docSnap.id;
                  if (!candidateBusinessIds.includes(resolvedId)) {
                    candidateBusinessIds.push(resolvedId);
                  }
                });
                // Always do a case-insensitive fallback scan.
                const allSnap = await db.collection('businesses').get();
                allSnap.forEach((docSnap: any) => {
                  const data = docSnap.data?.() || {};
                  const name = String(data.name || '').trim().toLowerCase();
                  if (name && name === normalizedNameLower) {
                    const resolvedId = docSnap.id;
                    if (!candidateBusinessIds.includes(resolvedId)) {
                      candidateBusinessIds.push(resolvedId);
                    }
                  }
                });
              }
            }
          }

          for (const tryBusinessId of candidateBusinessIds) {
            if (!tryBusinessId) continue;
            const loaded = await tryLoadServices(tryBusinessId);
            if (loaded) break;
          }
        } catch (resolveError) {
          console.warn('⚠️ Name-based business id resolution failed:', resolveError);
        }
      }
      
      // Fallback to local storage if Firestore didn't work (same pattern as products)
      // Try both business IDs for local storage lookup
      if (businessServices.length === 0) {
        console.log('⚠️ No services found in Firestore, trying local storage...');
        const businessIdsToTry = candidateBusinessIds.filter(Boolean);
        for (const tryBusinessId of businessIdsToTry) {
          if (!tryBusinessId) continue;
          try {
            const SimpleStorage = require('../utils/simpleStorage').default;
            const savedServices = await SimpleStorage.getItem(`services_${tryBusinessId}`);
            if (savedServices) {
              const localServices = JSON.parse(savedServices);
              console.log(`✅ Found services in local storage for ${tryBusinessId}:`, localServices.length);
              
              // Convert local service format to QuickBookOption format (only active services)
              businessServices = localServices
                .filter((svc: any) => svc.isActive !== false)
                .map((svc: any) => ({
                  id: svc.id || `service_${svc.name}`,
                  serviceName: svc.name || 'Service',
                  duration: svc.duration || 60,
                  price: normalizeStoredServicePrice(svc.price),
                  description: svc.description || '',
                  icon: svc.icon || '⚙️',
                  category: business.category || business.type || '',
                  isPopular: svc.isPopular || false,
                  availableSlots: [],
                  requirements: svc.requirements || [],
                }));
              console.log('✅ Loaded services from local storage:', businessServices.length);
              break; // Found services, stop trying other IDs
            } else {
              console.log(`⚠️ No services found in local storage for businessId: ${tryBusinessId}`);
            }
          } catch (error) {
            console.error(`❌ Error loading services from local storage for ${tryBusinessId}:`, error);
          }
        }
        if (businessServices.length === 0) {
          console.log('⚠️ No services found in local storage for any business ID');
        }
      }
      
      setServices(businessServices);
      
      // Products live in Firestore at businesses/{businessOwnerId}/products/all (same as ProductManagementScreen / web).
      // Prefer owner id first — that is where the owner dashboard saves; listing id may differ.
      const mapFirestoreProductsToQuickBook = (actualProducts: any[]): QuickBookOption[] =>
        actualProducts
          .filter((prod: any) => prod.isActive !== false && prod.isInStock !== false)
          .map((prod: any) => ({
            id: prod.id
              ? String(prod.id).startsWith('product_')
                ? String(prod.id)
                : `product_${prod.id}`
              : `product_${prod.name}`,
            serviceName: prod.name || 'Product',
            duration: prod.preparationTime ? parseInt(prod.preparationTime.toString()) || 0 : 0,
            price: prod.price || 0,
            description: prod.description || '',
            icon: prod.icon || '📦',
            category: prod.category || business.category || business.type || '',
            isPopular: prod.isPopular || false,
            availableSlots: [],
            requirements: [],
            isProduct: true,
            unit: prod.unit || '',
            stockQuantity: prod.stockQuantity,
            isInStock: prod.isInStock !== false,
            images: Array.isArray(prod.images) && prod.images.length > 0 ? prod.images : undefined,
            imageThumbnails: Array.isArray(prod.imageThumbnails) && prod.imageThumbnails.length > 0 ? prod.imageThumbnails : undefined,
            options: prod.options || undefined,
          }));

      const tryLoadProductsFromFirestore = async (bizId: string): Promise<QuickBookOption[]> => {
        if (!bizId) return [];
        try {
          let actualProducts: any[] = [];
          if (Platform.OS === 'web') {
            const { doc, getDoc } = require('firebase/firestore');
            const unifiedAuthService = require('../services/unifiedAuthService');
            const db = unifiedAuthService.getWebDb();
            if (!db) return [];
            const productsPath = `businesses/${bizId}/products/all`;
            console.log('🎯 Loading products from path:', productsPath);
            const productsDocRef = doc(db, 'businesses', bizId, 'products', 'all');
            const productsDoc = await getDoc(productsDocRef);
            console.log('🎯 Products document exists:', productsDoc.exists());
            if (!productsDoc.exists()) return [];
            const data = productsDoc.data();
            actualProducts = data.products || [];
          } else {
            const { getMobileFirestore, initializeFirebase } = require('../services/unifiedAuthService');
            initializeFirebase();
            const getFirestore = getMobileFirestore?.();
            if (!getFirestore || typeof getFirestore !== 'function') return [];
            const db = getFirestore();
            if (!db || typeof db.collection !== 'function') return [];
            const productsPath = `businesses/${bizId}/products/all`;
            console.log('🎯 Loading products from Firestore (mobile):', productsPath);
            const productsDoc = await db
              .collection('businesses')
              .doc(bizId)
              .collection('products')
              .doc('all')
              .get();
            if (!productsDoc.exists) return [];
            const data = productsDoc.data() || {};
            actualProducts = data.products || [];
          }
          console.log('🎯 Raw products array length:', actualProducts.length);
          return mapFirestoreProductsToQuickBook(actualProducts);
        } catch (error: any) {
          console.error('❌ Error loading products from Firestore:', error?.message || error);
          return [];
        }
      };

      let businessProducts: QuickBookOption[] = [];
      const productIdsToTry = candidateBusinessIds.filter(Boolean) as string[];
      const uniqueProductIds = [...new Set(productIdsToTry)];
      for (const bizId of uniqueProductIds) {
        businessProducts = await tryLoadProductsFromFirestore(bizId);
        if (businessProducts.length > 0) {
          console.log('✅ Loaded products from Firestore for business id:', bizId, businessProducts.length);
          break;
        }
      }

      // Fallback to local storage if Firestore didn't work
      if (businessProducts.length === 0) {
        console.log('⚠️ No products found in Firestore, trying local storage...');
        try {
          const SimpleStorage = require('../utils/simpleStorage').default;
          for (const tryBusinessId of uniqueProductIds) {
            if (!tryBusinessId) continue;
            const savedProducts = await SimpleStorage.getItem(`products_${tryBusinessId}`);
            if (savedProducts) {
              const localProducts = JSON.parse(savedProducts);
              console.log(`✅ Found products in local storage for ${tryBusinessId}:`, localProducts.length);
              businessProducts = mapFirestoreProductsToQuickBook(localProducts);
              console.log('✅ Loaded products from local storage:', businessProducts.length);
              break;
            }
            console.log(`⚠️ No products found in local storage for businessId: ${tryBusinessId}`);
          }
        } catch (error) {
          console.error('❌ Error loading products from local storage:', error);
        }
      }
      
      setProducts(businessProducts);
      
      // Set filter based on what's available
      if (businessServices.length > 0 && businessProducts.length > 0) {
        setServiceProductFilter('all'); // Show both if both exist
      } else if (businessProducts.length > 0) {
        setServiceProductFilter('products'); // Show products if only products exist
      } else if (businessServices.length > 0) {
        setServiceProductFilter('services'); // Show services if only services exist
      } else {
        setServiceProductFilter('all'); // Default to all (will show empty state)
      }
      
      console.log('✅ Loaded services:', businessServices.length, 'products:', businessProducts.length);
      
      // Pre-select the business
      setSelectedBusiness(business);
      // Stay on service selection step (don't auto-advance)
      setCurrentStep('service');
      
    } catch (error) {
      console.error('❌ Error loading services for business:', error);
      // Don't use fallback - just set empty arrays
      setServices([]);
      setProducts([]);
      setServiceProductFilter('all');
      setSelectedBusiness(business);
      setCurrentStep('service');
    } finally {
      setLoading(false);
    }
  };

  const addRequirementsToServices = (services: any[]) => {
    return services.map(service => ({
      ...service,
      requirements: service.requirements || ['Valid ID']
    }));
  };

  const getServicesForCategory = (categoryId: string) => {
    const servicesByCategory = {
      // Map new category IDs to services
      'automotive': [
        { id: 'oil_change', serviceName: 'Oil Change', duration: 30, price: 35, description: 'Standard oil change', icon: '🛢️', category: 'auto', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'tire_rotation', serviceName: 'Tire Rotation', duration: 45, price: 25, description: 'Rotate tires for even wear', icon: '🛞', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
        { id: 'brake_check', serviceName: 'Brake Inspection', duration: 60, price: 50, description: 'Complete brake system check', icon: '🛑', category: 'auto', isPopular: true, availableSlots: ['09:00', '11:00', '14:00', '16:00'] },
        { id: 'battery_test', serviceName: 'Battery Test', duration: 15, price: 15, description: 'Test battery health', icon: '🔋', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      ],
      'beauty_wellness': [
        { id: 'haircut_basic', serviceName: 'Basic Haircut', duration: 30, price: 45, description: 'Professional haircut and styling', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'haircut_wash', serviceName: 'Haircut + Wash', duration: 45, price: 60, description: 'Haircut with wash and style', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
        { id: 'hair_color', serviceName: 'Hair Coloring', duration: 120, price: 120, description: 'Professional hair coloring', icon: '🎨', category: 'hair', isPopular: false, availableSlots: ['09:00', '14:00'] },
        { id: 'hair_styling', serviceName: 'Hair Styling', duration: 60, price: 80, description: 'Special occasion styling', icon: '✨', category: 'hair', isPopular: false, availableSlots: ['10:00', '11:00', '15:00', '16:00'] },
        { id: 'massage_swedish', serviceName: 'Swedish Massage', duration: 60, price: 100, description: 'Relaxing full-body massage', icon: '🧘‍♀️', category: 'spa', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'facial_basic', serviceName: 'Basic Facial', duration: 60, price: 80, description: 'Standard facial treatment', icon: '✨', category: 'spa', isPopular: true, availableSlots: ['10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'manicure_basic', serviceName: 'Basic Manicure', duration: 30, price: 25, description: 'Standard manicure', icon: '💅', category: 'nails', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'pedicure_basic', serviceName: 'Basic Pedicure', duration: 45, price: 35, description: 'Standard pedicure', icon: '🦶', category: 'nails', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      ],
      // Map common business category names to their service groups
      'Hair Salon': [
        { id: 'haircut_basic', serviceName: 'Basic Haircut', duration: 30, price: 45, description: 'Professional haircut and styling', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'haircut_wash', serviceName: 'Haircut + Wash', duration: 45, price: 60, description: 'Haircut with wash and style', icon: '💇‍♀️', category: 'hair', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
        { id: 'hair_color', serviceName: 'Hair Coloring', duration: 120, price: 120, description: 'Professional hair coloring', icon: '🎨', category: 'hair', isPopular: false, availableSlots: ['09:00', '14:00'] },
        { id: 'hair_styling', serviceName: 'Hair Styling', duration: 60, price: 80, description: 'Special occasion styling', icon: '✨', category: 'hair', isPopular: false, availableSlots: ['10:00', '11:00', '15:00', '16:00'] },
      ],
      'Auto Service': [
        { id: 'oil_change', serviceName: 'Oil Change', duration: 30, price: 35, description: 'Standard oil change', icon: '🛢️', category: 'auto', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'tire_rotation', serviceName: 'Tire Rotation', duration: 45, price: 25, description: 'Rotate tires for even wear', icon: '🛞', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
        { id: 'brake_check', serviceName: 'Brake Inspection', duration: 60, price: 50, description: 'Complete brake system check', icon: '🛑', category: 'auto', isPopular: true, availableSlots: ['09:00', '11:00', '14:00', '16:00'] },
        { id: 'battery_test', serviceName: 'Battery Test', duration: 15, price: 15, description: 'Test battery health', icon: '🔋', category: 'auto', isPopular: false, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
      ],
      'health_fitness': [
        { id: 'personal_training', serviceName: 'Personal Training', duration: 60, price: 75, description: '1-on-1 fitness training', icon: '💪', category: 'fitness', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'yoga_class', serviceName: 'Yoga Class', duration: 60, price: 25, description: 'Group yoga session', icon: '🧘‍♀️', category: 'fitness', isPopular: true, availableSlots: ['09:00', '10:00', '17:00', '18:00'] },
        { id: 'pilates', serviceName: 'Pilates Class', duration: 60, price: 30, description: 'Core strengthening', icon: '🤸', category: 'fitness', isPopular: false, availableSlots: ['10:00', '14:00', '17:00'] },
      ],
      'medical_dental': [
        { id: 'checkup', serviceName: 'General Checkup', duration: 30, price: 150, description: 'Routine medical checkup', icon: '🏥', category: 'medical', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00'] },
        { id: 'dental_cleaning', serviceName: 'Dental Cleaning', duration: 60, price: 120, description: 'Professional teeth cleaning', icon: '🦷', category: 'dental', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      ],
      'home_garden': [
        { id: 'house_cleaning', serviceName: 'House Cleaning', duration: 120, price: 150, description: 'Complete house cleaning', icon: '🧹', category: 'cleaning', isPopular: true, availableSlots: ['09:00', '10:00', '14:00'] },
        { id: 'carpet_cleaning', serviceName: 'Carpet Cleaning', duration: 90, price: 100, description: 'Deep carpet cleaning', icon: '🧼', category: 'cleaning', isPopular: true, availableSlots: ['09:00', '14:00'] },
        { id: 'lawn_mowing', serviceName: 'Lawn Mowing', duration: 60, price: 50, description: 'Lawn mowing service', icon: '🌱', category: 'landscaping', isPopular: false, availableSlots: ['09:00', '10:00', '11:00', '14:00'] },
      ],
      'pet_care': [
        { id: 'dog_grooming', serviceName: 'Dog Grooming', duration: 90, price: 60, description: 'Full grooming service', icon: '🐕', category: 'pet', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00'] },
        { id: 'vet_visit', serviceName: 'Vet Checkup', duration: 45, price: 80, description: 'General health checkup', icon: '🏥', category: 'pet', isPopular: true, availableSlots: ['09:00', '10:00', '14:00', '15:00'] },
      ],
      'halal': [
        { id: 'halal_meat_cutting', serviceName: 'Fresh Halal Meat', duration: 30, price: 0, description: 'Fresh halal beef, chicken, lamb', icon: '🥩', category: 'halal_meat', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'] },
        { id: 'halal_dining', serviceName: 'Halal Restaurant Booking', duration: 60, price: 25, description: 'Reserve table at halal restaurant', icon: '🍽️', category: 'halal_restaurant', isPopular: true, availableSlots: ['12:00', '13:00', '18:00', '19:00', '20:00'] },
        { id: 'halal_grocery_order', serviceName: 'Halal Grocery Order', duration: 30, price: 0, description: 'Order halal groceries for pickup', icon: '🛒', category: 'halal_grocery', isPopular: true, availableSlots: ['09:00', '10:00', '11:00', '14:00', '15:00'] },
        { id: 'halal_catering_quote', serviceName: 'Halal Catering Quote', duration: 45, price: 0, description: 'Get quote for halal event catering', icon: '🍱', category: 'halal_catering', isPopular: false, availableSlots: ['10:00', '14:00', '15:00'] },
        { id: 'halal_bakery_order', serviceName: 'Halal Bakery Order', duration: 20, price: 0, description: 'Order halal baked goods', icon: '🥖', category: 'halal_bakery', isPopular: false, availableSlots: ['09:00', '10:00', '11:00', '14:00'] },
      ],
    };
    
    console.log('Getting services for category:', categoryId);
    const services = servicesByCategory[categoryId] || [];
    console.log('Found services:', services.length);
    
    return addRequirementsToServices(services);
  };

  const checkSlotAvailability = async () => {
    if (!selectedService || !selectedDate || !selectedTime) return;

    try {
      // Check if slot is available
      const isAvailable = Math.random() > 0.3; // Simulate availability check
      setIsSlotAvailable(isAvailable);

      if (!isAvailable) {
        // Check if we can add to waitlist
        const waitlistEntries = await waitlistService.getWaitlistEntries('test_business_1');
        const existingWaitlist = waitlistEntries.find(entry => 
          entry.serviceId === selectedService.id &&
          entry.requestedDate === selectedDate &&
          entry.requestedTime === selectedTime &&
          entry.status === 'waiting'
        );

        if (existingWaitlist) {
          Alert.alert(
            'Slot Unavailable',
            `This time slot is fully booked. You're position #${existingWaitlist.position} on the waitlist.`
          );
        }
      }
    } catch (error) {
      console.error('Error checking slot availability:', error);
    }
  };

  const handleAddToWaitlist = async () => {
    if (!selectedService || !selectedDate || !selectedTime) return;

    try {
      const waitlistEntry = await waitlistService.addToWaitlist({
        customerId: userId,
        businessId: 'test_business_1',
        serviceId: selectedService.id,
        serviceName: selectedService.serviceName,
        requestedDate: selectedDate,
        requestedTime: selectedTime,
        duration: selectedService.duration,
        price: selectedService.price ?? 0,
        customerData: {
          name: customerData.name,
          phone: customerData.phone,
          email: customerData.email,
          preferences: []
        },
        businessData: {
          name: 'Elite Hair Studio',
          address: '123 Main St, Arlington, TX 76010',
          phone: '(555) 123-4567',
          email: 'info@elitehair.com'
        },
        serviceData: {
          name: selectedService.serviceName,
          duration: selectedService.duration,
          price: selectedService.price ?? 0,
          description: selectedService.description,
          requirements: selectedService.requirements || []
        }
      });

      if (waitlistEntry) {
        Alert.alert(
          'Added to Waitlist',
          `You've been added to the waitlist for ${selectedService.serviceName}. You're position #${waitlistEntry.position}. We'll notify you when a slot becomes available.`
        );
        onBack();
      } else {
        Alert.alert('Error', 'Failed to add to waitlist. Please try again.');
      }
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      Alert.alert('Error', 'Failed to add to waitlist. Please try again.');
    }
  };

  // Step navigation functions
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    // This will trigger useEffect to load businesses
  };

  // Get quantity for an item
  const getItemQuantity = (itemId: string): number => {
    return itemQuantities[itemId] || 0;
  };

  const getBookingLineItemsForTotal = (): QuickBookOption[] => {
    let items = selectedItems.length > 0 ? [...selectedItems] : selectedService ? [selectedService] : [];
    if (selectedService && !items.find((i) => i.id === selectedService.id)) {
      return [selectedService, ...items];
    }
    return items;
  };

  const computePayNowTotalAmount = (): number => {
    return getBookingLineItemsForTotal().reduce((sum, item) => {
      const quantity = getItemQuantity(item.id) || 1;
      return sum + servicePriceForTotal(item.price) * quantity;
    }, 0);
  };

  // A product needs the options modal whenever it has any configured option
  // group, required or not, so customers can still choose optional options
  // (e.g. packaging) before adding it.
  const productHasOptions = (item: QuickBookOption): boolean =>
    !!item.isProduct && Array.isArray(item.options) && item.options.length > 0;

  // Increase quantity for a product
  const increaseQuantity = (item: QuickBookOption) => {
    if (productHasOptions(item) && getItemQuantity(item.id) === 0) {
      handleAddItem(item);
      return;
    }

    const currentQuantity = getItemQuantity(item.id);
    const newQuantity = currentQuantity + 1;
    
    setItemQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // Add to selectedItems if not already there
    if (!selectedItems.some(selected => selected.id === item.id)) {
      setSelectedItems([...selectedItems, item]);
    }
  };

  // Decrease quantity for a product
  const decreaseQuantity = (item: QuickBookOption) => {
    const currentQuantity = getItemQuantity(item.id);
    
    if (currentQuantity > 1) {
      // Decrease quantity
      setItemQuantities(prev => ({
        ...prev,
        [item.id]: currentQuantity - 1
      }));
    } else {
      // Remove from selection if quantity would be 0
      setItemQuantities(prev => {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      });
      setSelectedItems(selectedItems.filter(selected => selected.id !== item.id));
    }
  };

  // Toggle item selection (for services - no quantity)
  const handleToggleItemSelection = (item: QuickBookOption) => {
    if (item.isProduct) {
      // For products, use quantity controls
      const quantity = getItemQuantity(item.id);
      if (quantity > 0) {
        decreaseQuantity(item);
      } else {
        increaseQuantity(item);
      }
    } else {
      // For services, toggle selection (no quantity)
      const isSelected = selectedItems.some(selected => selected.id === item.id);
      if (isSelected) {
        setSelectedItems(selectedItems.filter(selected => selected.id !== item.id));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    }
  };

  // Check if item is selected
  const isItemSelected = (itemId: string) => {
    return selectedItems.some(item => item.id === itemId);
  };

  // Handle "Add" button click (for products with options)
  const handleAddItem = (item: QuickBookOption) => {
    // If product has any options, show options modal first
    if (productHasOptions(item)) {
      setSelectedService(item); // Set for options modal
      setSelectedProductOptions({}); // Reset selections
      setProductOptionsQuantity(1); // Reset quantity to 1
      setShowProductOptionsModal(true);
      return;
    }
    
    // For products without options, increase quantity
    increaseQuantity(item);
  };

  // Original handleServiceSelect (for single selection - services)
  const handleServiceSelect = (service: QuickBookOption) => {
    // For services, use single selection (direct booking)
    setSelectedService(service);
    
    // If this is a product with any options, show options modal first
    if (productHasOptions(service)) {
      setSelectedProductOptions({}); // Reset selections
      setProductOptionsQuantity(1); // Reset quantity to 1
      setShowProductOptionsModal(true);
      return;
    }

    // For services or products without options, proceed to fulfillment or availability
    proceedAfterServiceSelection();
  };

  // After widget deep link: preSelectedServiceId + loaded services → same path as tapping the service
  useEffect(() => {
    if (!preSelectedServiceId || !preSelectedBusiness) return;
    if (currentStep !== 'service') return;
    if (widgetServiceAutoAppliedRef.current === preSelectedServiceId) return;
    const combined = [...services, ...products];
    const match = combined.find((x) => x.id === preSelectedServiceId);
    if (!match) return;
    widgetServiceAutoAppliedRef.current = preSelectedServiceId;

    const widgetPick = consumeWidgetProductPick(
      String(preSelectedBusiness.id || ''),
      preSelectedServiceId,
    );
    if (widgetPick) {
      setSelectedService(match);
      setItemQuantities((prev) => ({
        ...prev,
        [match.id]: widgetPick.quantity,
      }));
      setItemSelectedOptions((prev) => ({
        ...prev,
        [match.id]: { ...widgetPick.selectedOptions },
      }));
      setSelectedItems([match]);
      setSelectedDate((prev) => {
        if (prev) return prev;
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      });
      proceedAfterServiceSelection();
      return;
    }

    setSelectedService(match);
    if (productHasOptions(match)) {
      setSelectedProductOptions({});
      setProductOptionsQuantity(1);
      setShowProductOptionsModal(true);
      return;
    }
    setSelectedDate((prev) => {
      if (prev) return prev;
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    });
    proceedAfterServiceSelection();
  }, [
    preSelectedServiceId,
    preSelectedBusiness,
    currentStep,
    services,
    products,
  ]);

  useEffect(() => {
    widgetServiceAutoAppliedRef.current = null;
  }, [preSelectedServiceId, preSelectedBusiness?.id]);

  const handleBusinessSelect = async (business: any) => {
    console.log('Business selected:', business.name);
    setSelectedBusiness(business);
    
    // Initialize default workers for this business if needed
    try {
      await workerService.initializeDefaultWorkers(business.id, business.name || 'Business');
      console.log(`✅ Ensured workers exist for business ${business.id}`);
    } catch (error) {
      console.error('Error initializing workers:', error);
    }
    
    // Load services for this business
    await loadServicesForBusiness(business);
    
    setCurrentStep('service');
  };

  const loadWorkersForBusiness = async (businessId: string) => {
    try {
      console.log('👥 Loading workers for business:', businessId);
      let workers = await workerService.getWorkers(businessId);
      
      // If no workers found, create default workers (solo or multiple based on business type)
      if (workers.length === 0) {
        console.log('📋 No workers found, initializing default workers...');
        // For now, assume solo business if no workers exist
        // In a real app, you'd check business owner's company size or preferences
        await workerService.initializeDefaultWorkers(businessId, 'Business Owner', true);
        workers = await workerService.getWorkers(businessId);
      }
      
      setAvailableWorkers(workers);
      console.log(`✅ Loaded ${workers.length} workers for business`);
    } catch (error) {
      console.error('Error loading workers for business:', error);
      setAvailableWorkers([]);
    }
  };

  const generateTimeSlots = async () => {
    try {
      console.log('⏰ Generating time slots...');
      if (selectedBusiness && selectedDate) {
        await loadAvailableSlotsWithWorkers(selectedBusiness.id, selectedDate);
      }
    } catch (error) {
      console.error('Error generating time slots:', error);
    }
  };

  const loadAvailableSlotsWithWorkers = async (businessId: string, date: string) => {
    try {
      setLoading(true);
      console.log('📋 Loading available time slots with worker availability...');
      
      let businessHours = bookingBusinessHours || selectedBusiness?.businessHours;
      let appointmentDuration =
        bookingAppointmentDuration ?? selectedBusiness?.appointmentDuration;

      if (selectedBusiness?.isOnBookAPT && !businessHours) {
        const ownerId = selectedBusiness.ownerId || selectedBusiness.id || businessId;
        const fromFirestore = await fetchBusinessBookingSettings(ownerId);
        businessHours = fromFirestore.businessHours;
        appointmentDuration = fromFirestore.appointmentDuration ?? appointmentDuration;

        if (!businessHours) {
          try {
            const businessOwner = await AuthManager.getSavedBusinessOwner();
            if (
              businessOwner &&
              (businessOwner.id === selectedBusiness.id || businessOwner.id === selectedBusiness.ownerId)
            ) {
              businessHours = businessOwner.businessHours;
              appointmentDuration = businessOwner.appointmentDuration ?? appointmentDuration;
            }
          } catch (error) {
            console.warn('⚠️ Could not load business owner data for hours:', error);
          }
        }

        if (businessHours) {
          setBookingBusinessHours(businessHours);
          setBookingAppointmentDuration(appointmentDuration);
        }
      }

      if (businessHours && !isBusinessOpenOnDate(businessHours, date)) {
        setAvailableSlots([]);
        console.log(`✅ Business closed on ${date} — no time slots`);
        return;
      }

      console.log('📋 Using business hours and duration:', { businessHours, appointmentDuration });
      
      const slotsWithWorkers = await availabilityService.generateTimeSlotsWithWorkers(
        businessId,
        date,
        undefined,
        undefined,
        undefined,
        businessHours,
        appointmentDuration
      );
      
      setAvailableSlots(slotsWithWorkers);
      console.log(`✅ Loaded ${slotsWithWorkers.length} available time slots`);

      const dayIsOpen = !businessHours || isBusinessOpenOnDate(businessHours, date);
      if (guestMode && dayIsOpen && slotsWithWorkers.length === 0) {
        setAvailableSlots(buildGuestWidgetFallbackSlots());
        console.log('✅ Guest widget: using fallback availability slots');
      }
    } catch (error) {
      console.error('Error loading available slots:', error);
      if (guestMode) {
        setAvailableSlots(buildGuestWidgetFallbackSlots());
        console.log('✅ Guest widget: fallback slots after availability error');
      } else {
        setAvailableSlots([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTimeSelect = async (timeSlot: TimeSlotWithWorkers) => {
    setSelectedTime(timeSlot.time);
    setAvailableWorkers(timeSlot.availableWorkers);
    
    // Auto-select worker if only one available
    if (timeSlot.availableWorkers.length === 1) {
      setSelectedWorker(timeSlot.availableWorkers[0]);
      console.log('✅ Auto-selected worker:', timeSlot.availableWorkers[0].name);
      setCurrentStep('booking');
    } else if (timeSlot.availableWorkers.length > 1) {
      // User needs to select from multiple workers
      setSelectedWorker(null);
      setCurrentStep('booking');
    }
  };

  const handleWorkerSelect = (worker: Worker) => {
    setSelectedWorker(worker);
    console.log('✅ Worker selected:', worker.name);
  };

  const goBack = () => {
    switch (currentStep) {
      case 'business':
        setCurrentStep('category');
        setSelectedCategory('');
        setBusinesses([]);
        setSelectedBusiness(null);
        break;
      case 'service':
        if (guestMode && preSelectedBusiness) {
          onBack();
          break;
        }
        // Only go back to business if we have a selected business
        // Otherwise, go back to category (for 30-second booking flow)
        if (selectedBusiness) {
          setCurrentStep('business');
          setSelectedBusiness(null);
          setServices([]);
          setSelectedService(null);
          setSelectedItems([]); // Clear selected items when going back
          setItemQuantities({}); // Clear quantities when going back
        } else {
          // No business selected - go back to category (30-second booking flow)
          setCurrentStep('category');
          setSelectedCategory('');
          setServices([]);
          setSelectedService(null);
          setSelectedItems([]);
          setItemQuantities({});
        }
        break;
      case 'availability':
        if (fulfillmentType === 'delivery') {
          setCurrentStep('fulfillment');
        } else {
          setCurrentStep('service');
        }
        setAvailableSlots([]);
        setSelectedDate('');
        setSelectedTime('');
        break;
      case 'fulfillment':
        setCurrentStep('service');
        break;
      case 'booking':
        if (fulfillmentType === 'delivery') {
          setCurrentStep('fulfillment');
        } else {
          setCurrentStep('availability');
        }
        setSelectedTime('');
        break;
      default:
        onBack();
    }
  };

  const showBookingMessage = (title: string, message: string) => {
    showCenteredAlert(title, message);
  };

  const sendGuestBookingConfirmationEmail = (booking: InstantBooking) => {
    if (!guestMode) {
      return;
    }
    const email = booking.customerData?.email?.trim();
    if (!email) {
      return;
    }
    guestBookingConfirmationEmailService
      .sendBookingConfirmation({
        customer: {
          name: booking.customerData.name || 'Customer',
          email,
        },
        booking: {
          id: booking.id,
          serviceName: booking.serviceName,
          businessName: booking.businessData?.name || selectedBusiness?.name || 'Business',
          appointmentDate: booking.appointmentDate,
          appointmentTime: booking.appointmentTime,
          amount: booking.amount,
          paymentMethod: booking.paymentMethod,
          businessEmail:
            selectedBusiness?.email ||
            booking.businessData?.email ||
            undefined,
          businessAddress:
            selectedBusiness?.address ||
            booking.businessData?.address ||
            undefined,
          businessPhone:
            selectedBusiness?.phone ||
            booking.businessData?.phone ||
            undefined,
        },
      })
      .catch((err) => {
        console.error('Failed to send guest booking confirmation email:', err);
      });
  };

  const runPayNowWithPayPal = async () => {
    if (!selectedService || !selectedDate || !selectedTime) {
      showBookingMessage('Error', 'Please select a service, date, and time');
      return;
    }

    const payNowTotal = computePayNowTotalAmount();
    if (payNowTotal <= 0) {
      showBookingMessage(
        'No online payment amount',
        'There is no fixed price to pay online for this selection. Use Confirm booking to submit; the business will confirm the amount.',
      );
      return;
    }

    const businessId = selectedBusiness?.id;
    if (!businessId) {
      showBookingMessage('Error', 'Select a business to continue.');
      return;
    }

    if (!validateAutomotiveBeforeConfirm()) {
      return;
    }

    const resolvedCustomerData = await resolveCustomerContactData();

    let effectiveUserId = userId;
    if (guestMode) {
      if (user && (user.id || (user as any).uid)) {
        effectiveUserId = user.id || (user as any).uid;
        rememberGuestAccountForGarage(effectiveUserId);
      } else if (guestAuthMode === 'returning') {
        if (!resolvedCustomerData.email && !customerData.email) {
          showBookingMessage('Error', 'Please enter your email to sign in.');
          return;
        }
        const auth = await ensureGuestAuthenticated(
          {
            name: resolvedCustomerData.name || 'Customer',
            phone: resolvedCustomerData.phone || '',
            email: resolvedCustomerData.email || customerData.email,
          },
          guestPassword,
        );
        if (!auth) {
          return;
        }
        effectiveUserId = auth.userId;
        rememberGuestAccountForGarage(effectiveUserId);
        resolvedCustomerData.name = auth.user.name || resolvedCustomerData.name;
        resolvedCustomerData.phone = auth.user.phone || resolvedCustomerData.phone;
        resolvedCustomerData.email = auth.user.email || resolvedCustomerData.email;
      } else {
        if (
          !resolvedCustomerData.name ||
          !resolvedCustomerData.phone ||
          !resolvedCustomerData.email
        ) {
          showBookingMessage(
            'Error',
            'Please enter your name, phone, and email to confirm your booking.',
          );
          return;
        }
        const auth = await ensureGuestAuthenticated(resolvedCustomerData, guestPassword);
        if (!auth) {
          return;
        }
        effectiveUserId = auth.userId;
        rememberGuestAccountForGarage(effectiveUserId);
      }
    } else if (
      !resolvedCustomerData.name ||
      !resolvedCustomerData.phone ||
      !resolvedCustomerData.email
    ) {
      showBookingMessage(
        'Error',
        'Contact info is incomplete. Please update your name, phone, and email in Profile, then try again.',
      );
      return;
    }

    setLoading(true);
    try {
      const { fetchBusinessPayPalDirectUrl, openPayPalDirectPayment } = await import(
        '../services/paypalDirectPaymentService'
      );
      const hasPayPal = await fetchBusinessPayPalDirectUrl(businessId);
      if (!hasPayPal) {
        showBookingMessage(
          'PayPal not set up',
          'This business has not linked PayPal yet. Choose Confirm booking or contact the business.',
        );
        return;
      }

      const paypalOpen = await openPayPalDirectPayment({
        businessId,
        amountUsd: payNowTotal,
      });
      if (!paypalOpen.opened) {
        showBookingMessage('PayPal', paypalOpen.error || 'Could not open PayPal.');
        return;
      }

      await processBookingPayLater(resolvedCustomerData, {
        paymentMethod: 'paypal',
        bookingNote: `PayPal pay-now ($${payNowTotal.toFixed(2)} USD) — customer pays business on PayPal; BookAPT does not process funds.`,
        successExtra: `\n\n${CUSTOMER_PAYPAL_PAYMENT_HELP}`,
        customerId: effectiveUserId,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = async () => {
    console.log('🔵 handleBookNow called', {
      selectedService: selectedService?.id,
      selectedDate,
      selectedTime,
      selectedWorker: selectedWorker?.name,
      customerData,
      availableWorkers: availableWorkers.length,
      Platform: Platform.OS
    });

    if (!selectedService || !selectedDate || !selectedTime) {
      const errorMsg = 'Please select a service, date, and time';
      console.error('❌ handleBookNow validation failed:', errorMsg);
      showBookingMessage('Error', errorMsg);
      return;
    }

    if (!validateAutomotiveBeforeConfirm()) {
      return;
    }

    if (!validateGuestContactBeforeConfirm()) {
      return;
    }

    const payNowTotal = computePayNowTotalAmount();
    if (payNowTotal <= 0) {
      const errorMsg =
        'There is no fixed price to pay online for this selection. Use Confirm booking to submit; the business will confirm the amount.';
      showBookingMessage('No online payment amount', errorMsg);
      return;
    }

    // Auto-assign worker if none selected but workers are available
    if (!selectedWorker && availableWorkers.length > 0) {
      const autoAssignedWorker = availableWorkers[0]; // Assign first available worker
      setSelectedWorker(autoAssignedWorker);
      console.log('✅ Auto-assigned worker:', autoAssignedWorker.name);
      
      // For web, directly open payment modal. For mobile, show alert first
      if (Platform.OS === 'web') {
        console.log('🌐 Web: Auto-assigned worker, opening PayPal pay flow');
        await runPayNowWithPayPal();
      } else {
        Alert.alert(
          'Worker Assigned',
          `We've assigned ${autoAssignedWorker.name} to your appointment. You can always request a different worker when you arrive.`,
          [{ text: 'Continue', onPress: () => runPayNowWithPayPal() }],
        );
      }
      return;
    }

    if (availableWorkers.length === 0) {
      if (Platform.OS === 'web') {
        console.log('🌐 Web: No workers available, opening PayPal pay flow');
        await runPayNowWithPayPal();
      } else {
        Alert.alert(
          'No Workers Available',
          'No workers are currently available for this time slot. The business will assign someone when you arrive.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Continue Anyway', onPress: () => runPayNowWithPayPal() },
          ],
        );
      }
      return;
    }

    await runPayNowWithPayPal();
  };

  const promptCalendarConnection = async (booking: InstantBooking) => {
    Alert.alert(
      '📅 Connect Your Calendar',
      'Add this appointment to your calendar so you never miss it!',
      [
        {
          text: 'Skip',
          style: 'cancel',
          onPress: () => {
            // Just show success message without calendar
            showBookingSuccessMessage(booking, false);
          }
        },
        {
          text: 'Connect Calendar',
          onPress: async () => {
            // Show calendar options
            Alert.alert(
              'Choose Calendar',
              'Which calendar would you like to use?',
              [
                {
                  text: 'Apple Calendar',
                  onPress: async () => {
                    try {
                      const result = await calendarService.connectAppleCalendar();
                      if (result.success && result.calendarId) {
                        // Create the event
                        await calendarService.createCalendarEvent({
                          id: booking.id,
                          serviceName: booking.serviceName,
                          customerName: booking.customerData.name,
                          appointmentDate: booking.appointmentDate,
                          appointmentTime: booking.appointmentTime,
                          duration: selectedService?.duration || 30,
                          businessName: 'Elite Hair Studio',
                          businessAddress: '123 Main St, Arlington, TX 76010',
                          businessPhone: '(555) 123-4567',
                          notes: `Booked via BookAPT`
                        }, result.calendarId);
                        showBookingSuccessMessage(booking, true);
                      }
                    } catch (error) {
                      console.error('Error connecting Apple Calendar:', error);
                      showBookingSuccessMessage(booking, false);
                    }
                  }
                },
                {
                  text: 'Google Calendar',
                  onPress: async () => {
                    try {
                      await calendarService.connectGoogleCalendar();
                      Alert.alert('Setup', 'Google Calendar connection initiated. Complete the OAuth flow in your browser, then book another appointment to sync.');
                      showBookingSuccessMessage(booking, false);
                    } catch (error) {
                      console.error('Error connecting Google Calendar:', error);
                      showBookingSuccessMessage(booking, false);
                    }
                  }
                },
                {
                  text: 'Outlook Calendar',
                  onPress: async () => {
                    try {
                      await calendarService.connectOutlookCalendar();
                      Alert.alert('Setup', 'Outlook Calendar connection initiated. Complete the OAuth flow in your browser, then book another appointment to sync.');
                      showBookingSuccessMessage(booking, false);
                    } catch (error) {
                      console.error('Error connecting Outlook Calendar:', error);
                      showBookingSuccessMessage(booking, false);
                    }
                  }
                },
                {
                  text: 'Cancel',
                  style: 'cancel',
                  onPress: () => showBookingSuccessMessage(booking, false)
                }
              ]
            );
          }
        }
      ]
    );
  };

  const showBookingSuccessMessage = async (booking: InstantBooking, addedToCalendar: boolean) => {
    const message = `Payment processed successfully! Your ${booking.serviceName} appointment is confirmed for ${booking.appointmentDate} at ${booking.appointmentTime}${addedToCalendar ? ' and added to your calendar' : ''}\n\nView your appointment details in the Appointments tab.${guestMode ? '\n\nWe sent a confirmation email with a link to download the BookAPT app.' : ''}`;

    console.log('✅ showBookingSuccessMessage called:', { booking, addedToCalendar, Platform: Platform.OS });

    await finishSuccessfulBooking(booking, message, {
      accountUserId: guestAccountUserIdRef.current || booking.customerId,
      alertTitle: 'Payment & Booking Confirmed! 🎉',
    });
  };

  const handleBookNowPayLater = async () => {
    console.log('🔵 handleBookNowPayLater called', {
      selectedService: selectedService?.id,
      selectedDate,
      selectedTime,
      selectedWorker: selectedWorker?.name,
      availableWorkers: availableWorkers.length,
      customerData,
      Platform: Platform.OS
    });

    if (!selectedService || !selectedDate || !selectedTime) {
      const errorMsg = 'Please select a service, date, and time';
      console.error('❌ handleBookNowPayLater validation failed:', errorMsg);
      showBookingMessage('Error', errorMsg);
      return;
    }

    if (!validateAutomotiveBeforeConfirm()) {
      return;
    }

    if (!validateGuestContactBeforeConfirm()) {
      return;
    }

    // Auto-assign worker if none selected but workers are available
    if (!selectedWorker && availableWorkers.length > 0) {
      const autoAssignedWorker = availableWorkers[0]; // Assign first available worker
      setSelectedWorker(autoAssignedWorker);
      console.log('✅ Auto-assigned worker for pay later:', autoAssignedWorker.name);
    }

    // For logged-in users, contact information is pre-filled
    // Only validate if we don't have the required data
    const resolvedCustomerData = await resolveCustomerContactData();

    let effectiveUserId = userId;
    if (guestMode) {
      if (user && (user.id || (user as any).uid)) {
        effectiveUserId = user.id || (user as any).uid;
        rememberGuestAccountForGarage(effectiveUserId);
      } else if (guestAuthMode === 'returning') {
        if (!resolvedCustomerData.email && !customerData.email) {
          const errorMsg = 'Please enter your email to sign in.';
          showBookingMessage('Error', errorMsg);
          return;
        }
        const auth = await ensureGuestAuthenticated(
          {
            name: resolvedCustomerData.name || 'Customer',
            phone: resolvedCustomerData.phone || '',
            email: resolvedCustomerData.email || customerData.email,
          },
          guestPassword,
        );
        if (!auth) {
          return;
        }
        effectiveUserId = auth.userId;
        rememberGuestAccountForGarage(effectiveUserId);
        resolvedCustomerData.name = auth.user.name || resolvedCustomerData.name;
        resolvedCustomerData.phone = auth.user.phone || resolvedCustomerData.phone;
        resolvedCustomerData.email = auth.user.email || resolvedCustomerData.email;
      } else {
        if (
          !resolvedCustomerData.name ||
          !resolvedCustomerData.phone ||
          !resolvedCustomerData.email
        ) {
          const errorMsg = 'Please enter your name, phone, and email to confirm your booking.';
          showBookingMessage('Error', errorMsg);
          return;
        }
        const auth = await ensureGuestAuthenticated(resolvedCustomerData, guestPassword);
        if (!auth) {
          return;
        }
        effectiveUserId = auth.userId;
        rememberGuestAccountForGarage(effectiveUserId);
      }
    } else if (
      !resolvedCustomerData.name ||
      !resolvedCustomerData.phone ||
      !resolvedCustomerData.email
    ) {
      const errorMsg =
        'Contact info is incomplete. Please update your name, phone, and email in Profile, then try again.';
      console.error('❌ handleBookNowPayLater: Missing contact info');
      showBookingMessage('Error', errorMsg);
      return;
    }

    console.log('✅ handleBookNowPayLater: All validations passed, processing booking...');
    // Book without payment - process booking with "pay_later" payment method
    processBookingPayLater(resolvedCustomerData, { customerId: effectiveUserId });
  };

  const handlePaymentMethodSelect = (methodId: string) => {
    console.log('Payment method selected:', methodId);
    setSelectedPaymentMethod(methodId);
  };

  const processBooking = async () => {
    if (!selectedService || !selectedPaymentMethod) return;

    if (!validateAutomotiveBeforeConfirm()) {
      return;
    }

    setLoading(true);
    try {
      console.log('Starting booking process...', {
        selectedService: selectedService?.id,
        selectedDate,
        selectedTime,
        selectedPaymentMethod,
        selectedWorker: selectedWorker?.id,
        customerData
      });

      // Final validation: Check if worker is still available (race condition protection)
      // Note: This check can cause false positives with default workers, so temporarily disabled
      // The worker availability was already validated when the time slot was selected
      /*
      if (selectedWorker) {
        const isWorkerStillAvailable = await availabilityService.isWorkerAvailable(
          selectedBusiness?.id || 'test_business_1',
          selectedWorker.id,
          selectedDate,
          selectedTime
        );
        
        if (!isWorkerStillAvailable) {
          Alert.alert(
            'Worker No Longer Available',
            `${selectedWorker.name} is no longer available for this time slot. Please select a different time or worker.`,
            [{ text: 'OK', onPress: () => setLoading(false) }]
          );
          return;
        }
      }
      */

      // Process payment first
      const paymentResult = await processPayment();
      
      if (!paymentResult.success) {
        Alert.alert('Payment Failed', paymentResult.error || 'Unable to process payment. Please try again.');
        setLoading(false);
        return;
      }

      let booking;
      try {
        booking = await instantBookingService.createInstantBooking({
          customerId: userId,
          businessId: selectedBusiness?.id || 'test_business_1',
          serviceId: selectedService.id,
          appointmentDate: selectedDate,
          appointmentTime: selectedTime,
          paymentMethod: selectedPaymentMethod,
          workerId: selectedWorker?.id,
          workerName: selectedWorker?.name,
          customerData: {
            name: customerData.name,
            phone: customerData.phone,
            email: customerData.email,
            address: customerData.address,
            preferences: [],
            vehicleId: selectedVehicleId,
            vehicle: selectedVehicle
          },
          service: selectedService // Pass service data directly to avoid lookup
        });
      } catch (error: any) {
        if (error.message === 'APPOINTMENT_LIMIT_REACHED' && error.limitCheck) {
          setLimitCheck(error.limitCheck);
          setShowLimitModal(true);
          setLoading(false);
          return;
        }
        throw error; // Re-throw other errors
      }

      // Save to all_bookings for availability tracking
      if (booking) {
        // Handle multiple products: use selectedItems if available, otherwise use selectedService
        const itemsToProcess = selectedItems.length > 0 ? selectedItems : (selectedService ? [selectedService] : []);
        const productDetails: string[] = [];
        
        // Process each item (product) with its quantity and options
        itemsToProcess.forEach((item) => {
          const storedOptions = itemSelectedOptions[item.id];
          const quantity = getItemQuantity(item.id) || 1;
          let itemDetails = `Quantity: ${quantity} × ${item.serviceName}`;
          
          // Add options if available
          if (storedOptions && item.options && item.options.length > 0) {
            const optionsText = item.options
              .map((option: any) => {
                const selectedValueId = storedOptions[option.id];
                if (!selectedValueId) return null;
                const selectedValue = option.values?.find((v: any) => v.id === selectedValueId);
                if (!selectedValue) return null;
                const priceModifier = selectedValue.priceModifier && selectedValue.priceModifier !== 0
                  ? ` (${selectedValue.priceModifier > 0 ? '+' : ''}$${selectedValue.priceModifier.toFixed(2)})`
                  : '';
                return `${option.name}: ${selectedValue.name}${priceModifier}`;
              })
              .filter(Boolean)
              .join(', ');
            
            if (optionsText) {
              itemDetails += ` | Selected Options: ${optionsText}`;
            }
          }
          
          productDetails.push(itemDetails);
        });
        
        // Combine all product details
        const specialInstructions = productDetails.join(' || ');
        const vehicleNote = selectedVehicle ? `Vehicle: ${formatVehicleDetailsForNotes(selectedVehicle)}` : '';
        const combinedSpecialInstructions = mergeAutomotiveIntoInstructions(
          [specialInstructions, !isAutomotiveBooking ? vehicleNote : ''].filter(Boolean).join(' || '),
        );
        
        if (combinedSpecialInstructions) {
          booking.specialInstructions = combinedSpecialInstructions;
        }
        
        await availabilityService.saveBooking(booking);
        console.log('✅ processBooking: Booking saved to availability service');
        
        // Also save appointment for business owner
        try {
          const { appointmentManager } = await import('../utils/appointmentManager');

          // Get customer address from user object - can be string or object
          let customerAddress = 'Address not provided';
          if (user?.address) {
            if (typeof user.address === 'string') {
              customerAddress = user.address;
            } else if (typeof user.address === 'object') {
              const addr = user.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
              const parts = [
                addr.street,
                addr.city,
                addr.state,
                addr.zipcode
              ].filter(Boolean);
              customerAddress = parts.length > 0 ? parts.join(', ') : 'Address not provided';
            }
          }
          // Fallback to customerData address if user address not available
          if (customerAddress === 'Address not provided') {
            customerAddress = booking.customerData.address || customerData.address || 'Address not provided';
          }
          
          // Get business owner ID - for BookAPT businesses, selectedBusiness.id is the owner ID
          // booking.businessId is already set to selectedBusiness.id, so use that directly
          // Only use selectedBusiness.id if booking.businessId is not available
          const businessOwnerId = booking.businessId || (selectedBusiness?.isOnBookAPT ? selectedBusiness?.id : null) || selectedBusiness?.ownerId;
          console.log('🔍 processBooking: Setting businessOwnerId for appointment:', {
            businessOwnerId,
            selectedBusinessOwnerId: selectedBusiness?.ownerId,
            selectedBusinessId: selectedBusiness?.id,
            selectedBusinessIsOnBookAPT: selectedBusiness?.isOnBookAPT,
            bookingBusinessId: booking.businessId,
            selectedBusiness: selectedBusiness?.name
          });
          
          // Build service type string - if multiple products, list them all
          const serviceTypeString = itemsToProcess.length > 1 
            ? itemsToProcess.map(item => item.serviceName).join(', ')
            : booking.serviceName;
          
          // Get business hours if available from business owner
          let savedBusinessHours = undefined;
          if (selectedBusiness?.isOnBookAPT && businessOwnerId) {
            try {
              const { AuthManager } = await import('../utils/authManager');
              const businessOwner = await AuthManager.getSavedBusinessOwner();
              if (businessOwner && businessOwner.id === businessOwnerId && businessOwner.businessHours) {
                savedBusinessHours = businessOwner.businessHours;
              }
            } catch (error) {
              console.warn('⚠️ Could not load business hours for appointment:', error);
            }
          }
          
          // Total charged at checkout (align with booking summary when multiple items)
          let itemsForPaidAmount =
            selectedItems.length > 0 ? [...selectedItems] : selectedService ? [selectedService] : [];
          if (selectedService && !itemsForPaidAmount.find((i) => i.id === selectedService.id)) {
            itemsForPaidAmount = [selectedService, ...itemsForPaidAmount];
          }
          const paidAmountTotal = itemsForPaidAmount.reduce((sum, item) => {
            const quantity = getItemQuantity(item.id) || 1;
            return sum + servicePriceForTotal(item.price) * quantity;
          }, 0);
          const paymentDataForAppointment = {
            amount: paidAmountTotal,
            paymentMethod: selectedPaymentMethod,
            status: 'completed' as const,
            transactionId: paymentResult.transactionId,
            processedAt: new Date().toISOString(),
            ...(Platform.OS === 'web' &&
            (selectedPaymentMethod === 'apple_pay' || selectedPaymentMethod === 'google_pay')
              ? { demoFlow: true as const }
              : {}),
          };

          const appointmentData = {
            customerName: booking.customerData.name,
            customerId: userId || (user as any)?.id || (user as any)?.uid,
            phone: booking.customerData.phone,
            email: booking.customerData.email,
            address: customerAddress, // Always use customer address, never business address
            serviceType: serviceTypeString,
            appointmentDate: booking.appointmentDate,
            appointmentTime: booking.appointmentTime,
            status: 'confirmed' as const,
            specialInstructions: combinedSpecialInstructions,
            ...buildAutomotiveAppointmentExtras(),
            businessOwnerId: businessOwnerId,
            workerId: booking.workerId,
            workerName: booking.workerName,
            paymentData: paymentDataForAppointment,
            // Business information for user view
            businessName: selectedBusiness?.name || booking.businessData?.name,
            businessAddress: selectedBusiness?.address || booking.businessData?.address,
            businessPhone: selectedBusiness?.phone || booking.businessData?.phone,
            businessEmail: selectedBusiness?.email || booking.businessData?.email,
            businessHours: savedBusinessHours, // Include business hours in appointment
          };
          
          console.log('🔵 processBooking: Saving appointment with data:', {
            email: appointmentData.email,
            customerName: appointmentData.customerName,
            serviceType: appointmentData.serviceType,
            appointmentDate: appointmentData.appointmentDate,
            appointmentTime: appointmentData.appointmentTime,
            userId: userId,
            userEmail: user?.email,
            userName: user?.name,
            userUsername: user?.username
          });
          
          console.log('🔵 processBooking: About to save appointment with data:', {
            email: appointmentData.email,
            customerName: appointmentData.customerName,
            serviceType: appointmentData.serviceType,
            appointmentDate: appointmentData.appointmentDate,
            appointmentTime: appointmentData.appointmentTime,
            businessOwnerId: appointmentData.businessOwnerId
          });
          
          const savedAppointment = await appointmentManager.saveAppointment(appointmentData);
          console.log('✅ processBooking: Appointment saved successfully:', {
            id: savedAppointment.id,
            email: savedAppointment.email,
            customerName: savedAppointment.customerName,
            serviceType: savedAppointment.serviceType,
            appointmentDate: savedAppointment.appointmentDate,
            appointmentTime: savedAppointment.appointmentTime
          });
          
          // Deduct 1 credit for the booking
          if (userId && savedAppointment?.id) {
            try {
              const { creditsService } = await import('../services/creditsService');
              const creditResult = await creditsService.consumeCredit(userId, savedAppointment.id);
              if (creditResult.success) {
                console.log('✅ processBooking: Credit deducted successfully. New balance:', creditResult.credits?.availableCredits);
              } else {
                console.warn('⚠️ processBooking: Credit deduction failed:', creditResult.error);
                // Don't fail the booking if credit deduction fails - booking is still valid
              }
            } catch (creditError: any) {
              console.error('❌ processBooking: Error deducting credit:', creditError);
              // Don't throw - booking was created successfully, credit deduction failure is not critical
            }
          }
        } catch (error: any) {
          console.error('❌ processBooking: Error saving appointment:', error);
          console.error('❌ Error message:', error?.message);
          console.error('❌ Error code:', error?.code);
          console.error('❌ Error stack:', error?.stack);
          if (error?.limitCheck) {
            console.error('❌ Limit check details:', error.limitCheck);
          }
          // Don't throw - booking was created successfully, just appointment save failed
          // But log the error so we can debug
        }
      }

      // Update product stock quantity if this is a product booking
      if (selectedService && (selectedService.isProduct || selectedService.stockQuantity !== undefined)) {
        try {
          const bookedQuantity = getItemQuantity(selectedService.id) || 1;
          console.log(`📦 Updating stock for product ${selectedService.id}: decrementing by ${bookedQuantity}`);
          
          // Use productManagementService to load and save products
          const productManagementServiceModule = await import('../services/productManagementService');
          const productManagementService = productManagementServiceModule.default || productManagementServiceModule.productManagementService;
          const businessOwnerId = booking.businessId;
          
          if (businessOwnerId) {
            const products = await productManagementService.loadProducts(businessOwnerId);
            const productToUpdate = products.find(p => p.id === selectedService.id);
            
            if (productToUpdate && productToUpdate.stockQuantity !== undefined) {
              const currentStock = productToUpdate.stockQuantity;
              const newStock = Math.max(0, currentStock - bookedQuantity);
              
              productToUpdate.stockQuantity = newStock;
              productToUpdate.isInStock = newStock > 0;
              productToUpdate.updatedAt = new Date().toISOString();
              
              console.log(`📦 Updated stock: ${productToUpdate.name} from ${currentStock} to ${newStock}`);
              
              // Save updated products
              await productManagementService.saveProducts(businessOwnerId, products);
              console.log(`✅ Product stock updated for ${productToUpdate.name}. New stock: ${newStock}`);
            } else {
              console.warn(`⚠️  Product ${selectedService.id} not found in products list or has no stockQuantity`);
            }
          }
        } catch (error: any) {
          console.error('❌ processBooking: Error updating product stock:', error);
          // Don't throw - booking was successful, stock update failure is not critical
        }
      }

      console.log('Booking result:', booking);

      if (booking) {
        setShowPaymentModal(false);
        
        if (booking.status === 'confirmed') {
          // Add to calendar if enabled
          if (addToCalendar) {
            // Check if user has connected calendar
            if (!selectedCalendar || connectedCalendars.length === 0) {
              // Prompt to connect calendar
              await promptCalendarConnection(booking);
            } else {
              // Calendar already connected, create event
              try {
                await calendarService.createCalendarEvent({
                  id: booking.id,
                  serviceName: booking.serviceName,
                  customerName: booking.customerData.name,
                  appointmentDate: booking.appointmentDate,
                  appointmentTime: booking.appointmentTime,
                  duration: selectedService.duration,
                  businessName: 'Elite Hair Studio',
                  businessAddress: '123 Main St, Arlington, TX 76010',
                  businessPhone: '(555) 123-4567',
                  notes: `Booked via BookAPT - ${selectedService.description}`
                }, selectedCalendar);
              } catch (error) {
                console.error('Error adding to calendar:', error);
              }
            }
          }

          // Create recurring appointment if enabled
          if (isRecurring) {
            try {
              await recurringAppointmentsService.createRecurringAppointment({
                customerId: userId,
                businessId: 'test_business_1',
                serviceId: selectedService.id,
                serviceName: selectedService.serviceName,
                duration: selectedService.duration,
                price: selectedService.price ?? 0,
                frequency: recurringFrequency,
                dayOfWeek: new Date(selectedDate).getDay(),
                startDate: selectedDate,
                customerData: {
                  name: customerData.name,
                  phone: customerData.phone,
                  email: customerData.email,
                  preferences: []
                },
                businessData: {
                  name: 'Elite Hair Studio',
                  address: '123 Main St, Arlington, TX 76010',
                  phone: '(555) 123-4567',
                  email: 'info@elitehair.com'
                },
                serviceData: {
                  name: selectedService.serviceName,
                  duration: selectedService.duration,
                  price: selectedService.price ?? 0,
                  description: selectedService.description,
                  requirements: selectedService.requirements || []
                }
              });
            } catch (error) {
              console.error('Error creating recurring appointment:', error);
            }
          }

          // Use the new success message function
          const calendarAdded = addToCalendar && selectedCalendar && connectedCalendars.length > 0;
          showBookingSuccessMessage(booking, calendarAdded);
        } else {
          const errorMsg = 'Payment could not be processed. Please try again.';
          showBookingMessage('Booking Failed', errorMsg);
        }
      } else {
        const errorMsg = 'Failed to create booking. Please try again.';
        showBookingMessage('Error', errorMsg);
      }
    } catch (error: any) {
      console.error('❌ Error processing booking:', error);
      if (error.message === 'APPOINTMENT_LIMIT_REACHED' && error.limitCheck) {
        setLimitCheck(error.limitCheck);
        setShowLimitModal(true);
        return;
      }
      const errorMsg = 'An error occurred while processing your booking.';
      showBookingMessage('Error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const processBookingPayLater = async (
    resolvedCustomerData?: typeof customerData,
    overrides?: {
      paymentMethod?: string;
      bookingNote?: string;
      successExtra?: string;
      customerId?: string;
    }
  ) => {
    if (!selectedService) return;
    const effectiveCustomerData = resolvedCustomerData || customerData;

    setLoading(true);
    try {
      console.log('Starting pay later booking process...', {
        selectedService: selectedService?.id,
        selectedDate,
        selectedTime,
        selectedWorker: selectedWorker?.id,
        customerData: effectiveCustomerData
      });

      // Final validation: Check if worker is still available (race condition protection)
      // Skip this check for "pay later" bookings to avoid false positives
      // The worker availability was already validated when the time slot was selected
      // Note: This check can be re-enabled if needed, but currently causes issues with default workers
      /*
      if (selectedWorker) {
        const isWorkerStillAvailable = await availabilityService.isWorkerAvailable(
          selectedBusiness?.id || 'test_business_1',
          selectedWorker.id,
          selectedDate,
          selectedTime
        );
        
        if (!isWorkerStillAvailable) {
          const errorMsg = `${selectedWorker.name} is no longer available for this time slot. Please select a different time or worker.`;
            showBookingMessage('Worker No Longer Available', errorMsg);
          setLoading(false);
          return;
        }
      }
      */

      let booking;
      try {
        console.log('🔵 processBookingPayLater: Creating booking...');
        booking = await instantBookingService.createInstantBooking({
          customerId: overrides?.customerId || userId,
          businessId: selectedBusiness?.id || 'test_business_1',
          serviceId: selectedService.id,
          appointmentDate: selectedDate,
          appointmentTime: selectedTime,
          paymentMethod: overrides?.paymentMethod || 'pay_later',
        workerId: selectedWorker?.id,
        workerName: selectedWorker?.name,
        customerData: {
          name: effectiveCustomerData.name,
          phone: effectiveCustomerData.phone,
          email: effectiveCustomerData.email,
          address: effectiveCustomerData.address,
          preferences: [],
          vehicleId: selectedVehicleId,
          vehicle: selectedVehicle
        },
        service: selectedService // Pass service data directly to avoid lookup
      });
      console.log('✅ processBookingPayLater: Booking created:', booking);
      } catch (error: any) {
        console.error('❌ processBookingPayLater: Error creating booking:', error);
        if (error.message === 'APPOINTMENT_LIMIT_REACHED' && error.limitCheck) {
          setLimitCheck(error.limitCheck);
          setShowLimitModal(true);
          setLoading(false);
          return;
        }
        throw error; // Re-throw other errors
      }

      // Save to all_bookings for availability tracking
      if (booking) {
        // Format selected options as a string for business owner to see
        const currentItem = selectedService;
        const storedOptions = currentItem ? itemSelectedOptions[currentItem.id] : null;
        if (storedOptions && currentItem?.options) {
          const optionsText = currentItem.options
            .map((option: any) => {
              const selectedValueId = storedOptions[option.id];
              if (!selectedValueId) return null;
              const selectedValue = option.values?.find((v: any) => v.id === selectedValueId);
              if (!selectedValue) return null;
              const priceModifier = selectedValue.priceModifier && selectedValue.priceModifier !== 0
                ? ` (${selectedValue.priceModifier > 0 ? '+' : ''}$${selectedValue.priceModifier.toFixed(2)})`
                : '';
              return `${option.name}: ${selectedValue.name}${priceModifier}`;
            })
            .filter(Boolean)
            .join(', ');
          if (optionsText) {
            booking.specialInstructions = `Selected Options: ${optionsText}`;
          }
        }
        await availabilityService.saveBooking(booking);
        console.log('✅ processBookingPayLater: Booking saved to availability service');
      }

      console.log('✅ Pay later booking result:', booking);

      if (booking && booking.status === 'confirmed') {
        // Convert instant booking to Appointment and save it
        try {
          const { appointmentManager } = await import('../utils/appointmentManager');
          
          // Handle multiple products: use selectedItems if available, otherwise use selectedService
          const itemsToProcess = selectedItems.length > 0 ? selectedItems : (selectedService ? [selectedService] : []);
          let specialInstructions = '';
          
          if (itemsToProcess.length > 0) {
            const productDetails: string[] = [];
            
            // Process each item (product) with its quantity and options
            itemsToProcess.forEach((item) => {
              const storedOptions = itemSelectedOptions[item.id];
              const quantity = getItemQuantity(item.id) || 1;
              let itemDetails = `Quantity: ${quantity} × ${item.serviceName}`;
              
              // Add options if available
              if (storedOptions && item.options && item.options.length > 0) {
                const optionsText = item.options
                  .map((option: any) => {
                    const selectedValueId = storedOptions[option.id];
                    if (!selectedValueId) return null;
                    const selectedValue = option.values?.find((v: any) => v.id === selectedValueId);
                    if (!selectedValue) return null;
                    const priceModifier = selectedValue.priceModifier && selectedValue.priceModifier !== 0
                      ? ` (${selectedValue.priceModifier > 0 ? '+' : ''}$${selectedValue.priceModifier.toFixed(2)})`
                      : '';
                    return `${option.name}: ${selectedValue.name}${priceModifier}`;
                  })
                  .filter(Boolean)
                  .join(', ');
                
                if (optionsText) {
                  itemDetails += ` | Selected Options: ${optionsText}`;
                }
              }
              
              productDetails.push(itemDetails);
            });
            
            // Combine all product details
            specialInstructions = productDetails.join(' || ');
          } else if (booking.specialInstructions) {
            // Fallback to booking's special instructions if no items to process
            specialInstructions = booking.specialInstructions;
          }

          if (overrides?.bookingNote) {
            specialInstructions = specialInstructions
              ? `${overrides.bookingNote} | ${specialInstructions}`
              : overrides.bookingNote;
          }

          specialInstructions = mergeAutomotiveIntoInstructions(specialInstructions);
          
          // Get business owner ID - for BookAPT businesses, selectedBusiness.id is the owner ID
          // booking.businessId is already set to selectedBusiness.id, so use that directly
          // Only use selectedBusiness.id if booking.businessId is not available
          const businessOwnerId = booking.businessId || (selectedBusiness?.isOnBookAPT ? selectedBusiness?.id : null) || selectedBusiness?.ownerId;
          console.log('🔍 processBookingPayLater: Setting businessOwnerId for appointment:', {
            businessOwnerId,
            selectedBusinessOwnerId: selectedBusiness?.ownerId,
            selectedBusinessId: selectedBusiness?.id,
            selectedBusinessIsOnBookAPT: selectedBusiness?.isOnBookAPT,
            bookingBusinessId: booking.businessId,
            selectedBusiness: selectedBusiness?.name
          });
          
          // Get customer address from user object - can be string or object
          let customerAddress = 'Address not provided';
          if (user?.address) {
            if (typeof user.address === 'string') {
              customerAddress = user.address;
            } else if (typeof user.address === 'object') {
              const addr = user.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string };
              const parts = [
                addr.street,
                addr.city,
                addr.state,
                addr.zipcode
              ].filter(Boolean);
              customerAddress = parts.length > 0 ? parts.join(', ') : 'Address not provided';
            }
          }
          // Fallback to booking customerData address if user address not available
          if (customerAddress === 'Address not provided') {
            customerAddress = booking.customerData.address || effectiveCustomerData.address || 'Address not provided';
          }
          
          // Build service type string - if multiple products, list them all
          const serviceTypeString = itemsToProcess.length > 1 
            ? itemsToProcess.map(item => item.serviceName).join(', ')
            : booking.serviceName;
          
          // Get business hours if available from business owner
          let savedBusinessHours = undefined;
          if (selectedBusiness?.isOnBookAPT && businessOwnerId) {
            try {
              // Try to load from Firestore first
              if (Platform.OS === 'web') {
                const { doc, getDoc } = require('firebase/firestore');
                const { getWebDb, initializeFirebase } = require('../services/unifiedAuthService');
                
                // Ensure Firebase is initialized and get named database
                const initialized = initializeFirebase();
                if (initialized) {
                  const firestoreDb = getWebDb();
                  if (firestoreDb) {
                    const businessOwnerDocRef = doc(firestoreDb, 'businesses', businessOwnerId);
                    const businessOwnerDoc = await getDoc(businessOwnerDocRef);
                    if (businessOwnerDoc.exists()) {
                      const data = businessOwnerDoc.data();
                      if (data.businessHours) {
                        savedBusinessHours = data.businessHours;
                        console.log('✅ Loaded business hours from Firestore for appointment');
                      }
                    }
                  }
                }
              }
              
              // Fallback to local storage if Firestore didn't work
              if (!savedBusinessHours) {
                const { AuthManager } = await import('../utils/authManager');
                const businessOwner = await AuthManager.getSavedBusinessOwner();
                if (businessOwner && businessOwner.id === businessOwnerId && businessOwner.businessHours) {
                  savedBusinessHours = businessOwner.businessHours;
                  console.log('✅ Loaded business hours from local storage for appointment');
                }
              }
            } catch (error) {
              console.warn('⚠️ Could not load business hours for appointment:', error);
            }
          }
          
          const appointmentData = {
            customerName: booking.customerData.name,
            customerId: userId || (user as any)?.id || (user as any)?.uid,
            phone: booking.customerData.phone,
            email: booking.customerData.email,
            address: customerAddress,
            serviceType: serviceTypeString,
            appointmentDate: booking.appointmentDate,
            appointmentTime: booking.appointmentTime,
            status: 'confirmed' as const,
            specialInstructions: specialInstructions,
            ...buildAutomotiveAppointmentExtras(),
            businessOwnerId: businessOwnerId,
            workerId: booking.workerId,
            workerName: booking.workerName,
            // Business information for user view
            businessName: selectedBusiness?.name || booking.businessData?.name,
            businessAddress: selectedBusiness?.address || booking.businessData?.address,
            businessPhone: selectedBusiness?.phone || booking.businessData?.phone,
            businessEmail: selectedBusiness?.email || booking.businessData?.email,
            businessHours: savedBusinessHours, // Include business hours in appointment
          };
          
          console.log('🔵 processBookingPayLater: Saving appointment with data:', {
            email: appointmentData.email,
            customerName: appointmentData.customerName,
            serviceType: appointmentData.serviceType,
            appointmentDate: appointmentData.appointmentDate,
            appointmentTime: appointmentData.appointmentTime,
            userId: userId,
            userEmail: user?.email,
            userName: user?.name,
            userUsername: user?.username
          });
          
          const savedAppointment = await appointmentManager.saveAppointment(appointmentData);
          console.log('✅ processBookingPayLater: Appointment saved:', {
            id: savedAppointment.id,
            email: savedAppointment.email,
            customerName: savedAppointment.customerName,
            serviceType: savedAppointment.serviceType
          });
          
          // Deduct 1 credit for the booking
          if (userId && savedAppointment?.id) {
            try {
              const { creditsService } = await import('../services/creditsService');
              const creditResult = await creditsService.consumeCredit(userId, savedAppointment.id);
              if (creditResult.success) {
                console.log('✅ processBookingPayLater: Credit deducted successfully. New balance:', creditResult.credits?.availableCredits);
              } else {
                console.warn('⚠️ processBookingPayLater: Credit deduction failed:', creditResult.error);
                // Don't fail the booking if credit deduction fails - booking is still valid
              }
            } catch (creditError: any) {
              console.error('❌ processBookingPayLater: Error deducting credit:', creditError);
              // Don't throw - booking was created successfully, credit deduction failure is not critical
            }
          }
        } catch (error: any) {
          console.error('❌ processBookingPayLater: Error saving appointment:', error);
          console.error('❌ Error message:', error?.message);
          console.error('❌ Error code:', error?.code);
          console.error('❌ Error stack:', error?.stack);
          if (error?.limitCheck) {
            console.error('❌ Limit check details:', error.limitCheck);
          }
          // Don't throw - booking was created successfully, just appointment save failed
        }

        // Update product stock quantity if this is a product booking
        if (selectedService && (selectedService.isProduct || selectedService.stockQuantity !== undefined)) {
          try {
            const bookedQuantity = getItemQuantity(selectedService.id) || 1;
            console.log(`📦 Updating stock for product ${selectedService.id}: decrementing by ${bookedQuantity}`);
            
            // Use productManagementService to load and save products
            const productManagementServiceModule = await import('../services/productManagementService');
            const productManagementService = productManagementServiceModule.default || productManagementServiceModule.productManagementService;
            const businessOwnerId = booking.businessId;
            
            if (businessOwnerId && productManagementService) {
              console.log(`📦 Loading products for business owner: ${businessOwnerId}`);
              const products = await productManagementService.loadProducts(businessOwnerId);
              console.log(`📦 Loaded ${products.length} products for stock update`);
              console.log(`📦 Product IDs in list:`, products.map(p => p.id));
              console.log(`📦 Looking for product ID: ${selectedService.id}`);
              const productToUpdate = products.find(p => p.id === selectedService.id);
              
              if (productToUpdate) {
                console.log(`📦 Found product: ${productToUpdate.name}, current stock: ${productToUpdate.stockQuantity}`);
              } else {
                console.warn(`⚠️  Product ${selectedService.id} not found in products list`);
              }
              
              if (productToUpdate && productToUpdate.stockQuantity !== undefined) {
                const currentStock = productToUpdate.stockQuantity;
                const newStock = Math.max(0, currentStock - bookedQuantity);
                
                productToUpdate.stockQuantity = newStock;
                productToUpdate.isInStock = newStock > 0;
                productToUpdate.updatedAt = new Date().toISOString();
                
                console.log(`📦 Updated stock: ${productToUpdate.name} from ${currentStock} to ${newStock}`);
                
                // Save updated products
                await productManagementService.saveProducts(businessOwnerId, products);
                console.log(`✅ Product stock updated for ${productToUpdate.name}. New stock: ${newStock}`);
              } else {
                console.warn(`⚠️  Product ${selectedService.id} not found in products list or has no stockQuantity`);
              }
            }
          } catch (error: any) {
            console.error('❌ processBookingPayLater: Error updating product stock:', error);
            // Don't throw - booking was successful, stock update failure is not critical
          }
        }

        // Show success message and handle navigation
        console.log('🔵 processBookingPayLater: About to check booking status. booking =', booking);
        console.log('🔵 processBookingPayLater: booking.status =', booking?.status);
        if (booking && booking.status === 'confirmed') {
          console.log('✅ processBookingPayLater: Entered confirmed booking block');
          try {
            // Add to calendar if enabled
            console.log('🔵 processBookingPayLater: addToCalendar =', addToCalendar);
            if (addToCalendar) {
              // Check if user has connected calendar
              if (!selectedCalendar || connectedCalendars.length === 0) {
                // Prompt to connect calendar (but don't block navigation)
                console.log('🔵 processBookingPayLater: Prompting calendar connection...');
                promptCalendarConnection(booking).catch(err => {
                  console.error('Error prompting calendar connection:', err);
                });
                // Don't return - continue with navigation
            } else {
              // Calendar already connected, create event
              try {
                await calendarService.createCalendarEvent({
                  id: booking.id,
                  serviceName: booking.serviceName,
                  customerName: booking.customerData.name,
                  appointmentDate: booking.appointmentDate,
                  appointmentTime: booking.appointmentTime,
                  duration: selectedService.duration,
                  businessName: 'Elite Hair Studio',
                  businessAddress: '123 Main St, Arlington, TX 76010',
                  businessPhone: '(555) 123-4567',
                  notes: `Booked via BookAPT - ${selectedService.description} (Confirm booking)`
                }, selectedCalendar);
              } catch (error) {
                console.error('Error adding to calendar:', error);
              }
            }
          }

          // Create recurring appointment if enabled
          if (isRecurring) {
            try {
              await recurringAppointmentsService.createRecurringAppointment({
                customerId: userId,
                businessId: 'test_business_1',
                serviceId: selectedService.id,
                serviceName: selectedService.serviceName,
                duration: selectedService.duration,
                price: selectedService.price ?? 0,
                frequency: recurringFrequency,
                dayOfWeek: new Date(selectedDate).getDay(),
                startDate: selectedDate,
                customerData: {
                  name: effectiveCustomerData.name,
                  phone: effectiveCustomerData.phone,
                  email: effectiveCustomerData.email,
                  preferences: []
                },
                businessData: {
                  name: 'Elite Hair Studio',
                  address: '123 Main St, Arlington, TX 76010',
                  phone: '(555) 123-4567',
                  email: 'info@elitehair.com'
                },
                serviceData: {
                  name: selectedService.serviceName,
                  duration: selectedService.duration,
                  price: selectedService.price ?? 0,
                  description: selectedService.description,
                  requirements: selectedService.requirements || []
                }
              });
            } catch (error) {
              console.error('Error creating recurring appointment:', error);
            }
          }

          console.log('🔵 processBookingPayLater: Past calendar/recurring checks, about to start navigation...');
          // Show success message and handle navigation
          const calendarAdded = addToCalendar && selectedCalendar && connectedCalendars.length > 0;
          const paymentLine =
            overrides?.paymentMethod === 'paypal'
              ? 'Finish paying on PayPal if you have not already. The business confirms payment after PayPal notifies them.'
              : 'Payment is due at the time of service.';
          const successMessage = `Your ${booking.serviceName} appointment is confirmed for ${booking.appointmentDate} at ${booking.appointmentTime}. ${paymentLine}${calendarAdded ? ' Event added to your calendar.' : ''}${overrides?.successExtra || ''}\n\nView your appointment in the Appointments tab.${guestMode ? '\n\nWe sent a confirmation email with a link to download the BookAPT app.' : ''}`;
          
          console.log('🔵 processBookingPayLater: Starting navigation process...');
          console.log('🔵 processBookingPayLater: booking.status =', booking.status);
          console.log('🔵 processBookingPayLater: onBookingComplete exists?', !!onBookingComplete);

          await finishSuccessfulBooking(booking, successMessage, {
            accountUserId: guestAccountUserIdRef.current || overrides?.customerId,
          });
          } catch (error) {
            console.error('❌ processBookingPayLater: Error in confirmed booking block:', error);
            throw error;
          }
        } else {
          const errorMsg = 'Unable to confirm your booking. Please try again.';
          showBookingMessage('Booking Failed', errorMsg);
        }
      } else {
        const errorMsg = 'Failed to create booking. Please try again.';
        showBookingMessage('Error', errorMsg);
      }
    } catch (error: any) {
      console.error('❌ Error processing pay later booking:', error);
      if (error.message === 'APPOINTMENT_LIMIT_REACHED' && error.limitCheck) {
        setLimitCheck(error.limitCheck);
        setShowLimitModal(true);
        setLoading(false);
        return;
      }
      const errorMsg = 'An error occurred while processing your booking.';
      showBookingMessage('Error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    try {
      console.log('Processing payment with method:', selectedPaymentMethod);
      
      if (!selectedService) {
        return { success: false, error: 'No service selected' };
      }

      let itemsForPayment =
        selectedItems.length > 0 ? [...selectedItems] : selectedService ? [selectedService] : [];
      if (selectedService && !itemsForPayment.find((i) => i.id === selectedService.id)) {
        itemsForPayment = [selectedService, ...itemsForPayment];
      }
      const totalPayAmount = itemsForPayment.reduce((sum, item) => {
        const qty = getItemQuantity(item.id) || 1;
        return sum + servicePriceForTotal(item.price) * qty;
      }, 0);

      if (
        ['apple_pay', 'google_pay', 'credit_card', 'venmo'].includes(selectedPaymentMethod) &&
        totalPayAmount <= 0
      ) {
        return {
          success: false,
          error:
            'No online payment amount for this selection. Choose Confirm booking, or pick priced items or services.',
        };
      }

      // ----- WEB: route Apple Pay / Google Pay / Card through real Stripe -----
      if (
        Platform.OS === 'web' &&
        ['apple_pay', 'google_pay', 'credit_card'].includes(selectedPaymentMethod)
      ) {
        const serviceSummary =
          itemsForPayment.length > 1
            ? `${itemsForPayment.length} items`
            : selectedService.serviceName;

        const methodLabel =
          selectedPaymentMethod === 'apple_pay'
            ? 'Apple Pay'
            : selectedPaymentMethod === 'google_pay'
            ? 'Google Pay'
            : 'Card';

        const result = await openWebStripeCheckout(
          {
            businessId: selectedBusiness?.id || 'test_business_1',
            amount: totalPayAmount,
            currency: 'usd',
            customerId: userId,
            customerName: customerData?.name,
            customerEmail: customerData?.email,
            serviceName: serviceSummary,
          },
          methodLabel
        );

        if (result.cancelled || !result.success) {
          return {
            success: false,
            error: result.error || 'Payment was cancelled',
          };
        }

        return {
          success: true,
          transactionId: result.paymentIntentId,
          paymentMethod: selectedPaymentMethod,
        };
      }

      // Validate card details for credit card payments
      if (selectedPaymentMethod === 'credit_card') {
        const cleanCardNumber = cardNumber.replace(/\s/g, '');
        
        console.log('Validating card details:', {
          cardNumber,
          cleanCardNumber,
          expiryDate,
          cvc,
          cardholderName
        });
        
        if (!cleanCardNumber || !expiryDate || !cvc) {
          Alert.alert('Error', 'Please fill in all required card details:\n• Card Number\n• Expiry Date (MM/YY)\n• CVC');
          return { success: false, error: 'Missing card details' };
        }
        
        if (cleanCardNumber.length < 13) {
          Alert.alert('Error', 'Please enter a valid card number (13-19 digits)');
          return { success: false, error: 'Invalid card number' };
        }
        
        if (!expiryDate.match(/^\d{2}\/\d{2}$/)) {
          Alert.alert('Error', 'Please enter expiry date in MM/YY format');
          return { success: false, error: 'Invalid expiry date' };
        }
        
        if (cvc.length < 3) {
          Alert.alert('Error', 'Please enter a valid CVC (3 or 4 digits)');
          return { success: false, error: 'Invalid CVC' };
        }
        
        console.log('✅ Card validation passed');
      }

      let result;

      if (selectedPaymentMethod === 'credit_card') {
        // Use Stripe payment service for credit cards
        console.log('Processing credit card with Stripe...');
        
        // Parse expiry date (MM/YY)
        const [expMonth, expYear] = expiryDate.split('/').map(s => s.trim());
        const fullYear = expYear?.length === 2 ? `20${expYear}` : expYear;
        
        const cardDetails = {
          number: cardNumber.replace(/\s/g, ''),
          expMonth: parseInt(expMonth) || 12,
          expYear: parseInt(fullYear) || 2025,
          cvc: cvc,
          name: cardholderName || 'Cardholder',
          postalCode: zipCode || '00000',
        };

        const paymentRequest = {
          amount: totalPayAmount,
          currency: 'USD',
          serviceName: selectedService.serviceName,
          customerId: userId,
          businessId: selectedBusiness?.id || 'test_business_1',
        };

        result = await stripePaymentService.processCreditCard(paymentRequest, cardDetails);
      } else {
        // Use existing payment service for other methods
        const paymentRequest = {
          amount: totalPayAmount,
          currency: 'USD',
          serviceName: selectedService.serviceName,
          customerId: userId,
          businessId: selectedBusiness?.id || 'test_business_1',
          paymentMethod: selectedPaymentMethod as 'apple_pay' | 'google_pay' | 'credit_card' | 'venmo'
        };

        switch (selectedPaymentMethod) {
          case 'apple_pay':
            result = await realPaymentService.processApplePay(paymentRequest);
            break;
          case 'google_pay':
            result = await realPaymentService.processGooglePay(paymentRequest);
            break;
          case 'venmo':
            // Use Braintree for Venmo payments
            result = await braintreeService.processVenmoPayment({
              amount: totalPayAmount,
              serviceName: selectedService.serviceName,
              customerId: userId,
              businessId: selectedBusiness?.id || 'test_business_1',
              description: `${selectedService.serviceName} - ${selectedBusiness?.name || 'Business'}`,
            });
            break;
          default:
            return { success: false, error: 'Unsupported payment method' };
        }
      }

      return result;
    } catch (error) {
      console.error('Payment processing error:', error);
      return { success: false, error: 'Payment processing failed' };
    }
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        display: date.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    return dates;
  };

  const getTimeSlots = () => {
    return selectedService?.availableSlots || [];
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  const renderStepIndicator = () => {
    const showFulfillment =
      DELIVERY_ENABLED &&
      cartHasProducts() &&
      (merchantDeliveryEnabled || merchantPrepayRequired);
    const steps = guestMode
      ? [
          { key: 'service', label: 'Items' },
          ...(showFulfillment ? [{ key: 'fulfillment', label: 'Pickup / delivery' }] : []),
          ...(fulfillmentType === 'pickup'
            ? [{ key: 'availability', label: 'Date & time' }]
            : []),
          { key: 'booking', label: 'Confirm' },
        ]
      : [
          { key: 'category', label: 'Category' },
          { key: 'business', label: 'Business' },
          { key: 'service', label: 'Service' },
          ...(showFulfillment ? [{ key: 'fulfillment', label: 'Pickup / delivery' }] : []),
          ...(fulfillmentType === 'pickup'
            ? [{ key: 'availability', label: 'Availability' }]
            : []),
          { key: 'booking', label: 'Booking' },
        ];
    const currentStepIndex = Math.max(
      0,
      steps.findIndex((s) => s.key === currentStep)
    );
    
    return (
      <View style={styles.stepIndicator}>
        {steps.map((step, index) => (
          <View key={step.key} style={styles.stepContainer}>
            <View style={[
              styles.stepCircle,
              index <= currentStepIndex ? styles.stepCircleActive : styles.stepCircleInactive
            ]}>
              <Text style={[
                styles.stepNumber,
                index <= currentStepIndex ? styles.stepNumberActive : styles.stepNumberInactive
              ]}>
                {index + 1}
              </Text>
            </View>
            <Text style={[
              styles.stepLabel,
              index <= currentStepIndex ? styles.stepLabelActive : styles.stepLabelInactive
            ]}>
              {step.label}
            </Text>
          </View>
        ))}
      </View>
    );
  };

  const renderCategorySelection = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>What type of service do you need?</Text>
      <Text style={styles.stepSubtitle}>Search or choose a category</Text>
      
      {/* Search Field */}
      <View style={styles.searchContainer}>
        <Text style={styles.searchIcon}>🔍</Text>
        <AppTextInput
          style={styles.searchInput}
          placeholder="Type service you are looking for..."
          value={categorySearchQuery}
          onChangeText={setCategorySearchQuery}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {categorySearchQuery.length > 0 && (
          <TouchableOpacity 
            style={styles.clearSearchButton}
            onPress={() => setCategorySearchQuery('')}
          >
            <Text style={styles.clearSearchText}>✕</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Search Results Info */}
      {categorySearchQuery.trim() && (
        <Text style={styles.searchResultsText}>
          {filteredCategories.length > 0 
            ? `Found ${filteredCategories.length} ${filteredCategories.length === 1 ? 'category' : 'categories'}`
            : 'No categories found. Try a different search term.'}
        </Text>
      )}
      
      <View style={styles.categoriesGrid}>
        {displayedCategories.length > 0 ? (
          displayedCategories.map((category) => (
            <TouchableOpacity
              key={category.id}
              style={[
                styles.categoryCard,
                selectedCategory === category.id && styles.categoryCardSelected
              ]}
              onPress={() => handleCategorySelect(category.id)}
            >
              <Text style={styles.categoryIcon}>{category.icon}</Text>
              <Text style={styles.categoryName}>{category.name}</Text>
              <Text style={styles.categoryDescription}>{category.description}</Text>
            </TouchableOpacity>
          ))
        ) : (
          <View style={styles.noResultsContainer}>
            <Text style={styles.noResultsIcon}>🔍</Text>
            <Text style={styles.noResultsText}>No matching categories found</Text>
            <Text style={styles.noResultsSubtext}>Try searching for: "hair", "auto", "spa", "fitness"</Text>
          </View>
        )}
      </View>

      {/* Load More Button - Only show when not searching */}
      {!categorySearchQuery.trim() && !showAllCategories && BUSINESS_CATEGORIES.length > 8 && (
        <TouchableOpacity 
          style={styles.loadMoreButton}
          onPress={() => setShowAllCategories(true)}
        >
          <Text style={styles.loadMoreButtonText}>Load More Categories</Text>
          <Text style={styles.loadMoreIcon}>▼</Text>
        </TouchableOpacity>
      )}

      {/* Show Less Button - When all categories are shown */}
      {!categorySearchQuery.trim() && showAllCategories && (
        <TouchableOpacity 
          style={styles.showLessButton}
          onPress={() => setShowAllCategories(false)}
        >
          <Text style={styles.showLessButtonText}>Show Less</Text>
          <Text style={styles.showLessIcon}>▲</Text>
        </TouchableOpacity>
      )}

      {/* Button to explore businesses directly - passes selected category */}
      {selectedCategory && (
        <View style={styles.exploreMoreContainer}>
          <TouchableOpacity
            style={styles.exploreMoreButton}
            onPress={() => {
              if (onNavigateToBusinessExploration) {
                // Store the selected category so BusinessExplorationScreen can use it
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  sessionStorage.setItem('preselectedCategory', selectedCategory);
                  console.log('🔍 Stored preselected category from category selection:', selectedCategory);
                }
                onNavigateToBusinessExploration();
              }
            }}
          >
            <Text style={styles.exploreMoreButtonText}>🔍 Explore Businesses</Text>
            <Text style={styles.exploreMoreButtonSubtext}>Browse all businesses in this category</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  const renderGridCard = (item: QuickBookOption) => {
    const quantity = getItemQuantity(item.id);
    const isProduct = !!(item as any).isProduct;
    const productImageUri =
      isProduct && Array.isArray(item.images) && item.images.length > 0
        ? String((item.imageThumbnails && item.imageThumbnails[0]) || item.images[0] || '').trim()
        : '';
    const isSelected = selectedService?.id === item.id;

    return (
      <TouchableOpacity
        key={item.id}
        style={[styles.gridCard, { width: gridCardWidth }, isSelected && styles.serviceCardSelected]}
        onPress={() => (isProduct ? handleAddItem(item) : handleServiceSelect(item))}
      >
        <View style={styles.gridImageWrap}>
          {productImageUri ? (
            <Image
              source={{ uri: productImageUri }}
              style={styles.gridImage}
              resizeMode="cover"
              {...(Platform.OS === 'web' ? { referrerPolicy: 'no-referrer' as const } : {})}
            />
          ) : (
            <Text style={styles.gridIcon}>{item.icon}</Text>
          )}
          {item.isPopular && (
            <View style={[styles.popularBadge, styles.gridPopularBadge]}>
              <Text style={styles.popularText}>Popular</Text>
            </View>
          )}
        </View>
        <View style={styles.gridCardBody}>
          <Text style={styles.gridCardName} numberOfLines={2}>{item.serviceName}</Text>
          {isProduct ? (
            (item as any).isInStock === false ? (
              <Text style={[styles.serviceStock, styles.outOfStockText]}>Out of Stock</Text>
            ) : (item as any).unit ? (
              <Text style={styles.gridCardMeta}>Per {(item as any).unit}</Text>
            ) : null
          ) : (
            <Text style={styles.gridCardMeta}>{item.duration} min</Text>
          )}
          <View style={styles.gridCardFooterRow}>
            <Text style={styles.gridCardPrice}>{formatServicePriceShort(item.price)}</Text>
            {isProduct && (
              quantity > 0 ? (
                <View style={styles.gridQuantityControls}>
                  <TouchableOpacity
                    style={styles.gridQuantityButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      decreaseQuantity(item);
                    }}
                  >
                    <Text style={styles.quantityButtonText}>−</Text>
                  </TouchableOpacity>
                  <Text style={styles.quantityText}>{quantity}</Text>
                  <TouchableOpacity
                    style={styles.gridQuantityButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      increaseQuantity(item);
                    }}
                  >
                    <Text style={styles.quantityButtonText}>+</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  style={styles.gridAddButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    handleAddItem(item);
                  }}
                >
                  <Text style={styles.gridAddButtonText}>+</Text>
                </TouchableOpacity>
              )
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderServiceSelection = () => {
    // Determine what to display based on filter
    const displayItems = serviceProductFilter === 'services'
      ? services
      : serviceProductFilter === 'products'
      ? products
      : [...services, ...products]; // 'all' shows both

    const catalogCategories = Array.from(
      new Set(displayItems.map((i) => i.category).filter((c): c is string => !!c))
    ).sort((a, b) => a.localeCompare(b));

    const gridItems =
      catalogCategoryFilter === 'all'
        ? displayItems
        : displayItems.filter((i) => i.category === catalogCategoryFilter);

    const selectFilter = (filter: 'services' | 'products' | 'all') => {
      setServiceProductFilter(filter);
      setCatalogCategoryFilter('all'); // category set may differ under the new filter
    };

    return (
      <FlatList
        style={styles.stepContent}
        contentContainerStyle={styles.stepContentContainer}
        data={gridItems}
        numColumns={GRID_NUM_COLUMNS}
        keyExtractor={(item) => item.id}
        columnWrapperStyle={styles.gridRow}
        showsVerticalScrollIndicator={true}
        renderItem={({ item }) => renderGridCard(item)}
        ListHeaderComponent={
          <View style={styles.stepContainer}>
            <Text style={styles.stepTitle}>Select Service/Product</Text>
            <Text style={styles.stepSubtitle}>Choose what you need</Text>

            {/* Filter Selector - Always show buttons */}
            <View style={styles.filterSelector}>
              <Text style={styles.filterLabel}>Filter:</Text>
              <View style={styles.filterButtons}>
                <TouchableOpacity
                  style={[
                    styles.filterButton,
                    serviceProductFilter === 'all' && styles.filterButtonActive
                  ]}
                  onPress={() => selectFilter('all')}
                >
                  <Text style={[
                    styles.filterButtonText,
                    serviceProductFilter === 'all' && styles.filterButtonTextActive
                  ]}>All</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.filterButton,
                    serviceProductFilter === 'services' && styles.filterButtonActive
                  ]}
                  onPress={() => selectFilter('services')}
                >
                  <Text style={[
                    styles.filterButtonText,
                    serviceProductFilter === 'services' && styles.filterButtonTextActive
                  ]}>
                    Services{services.length > 0 ? ` (${services.length})` : ''}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.filterButton,
                    serviceProductFilter === 'products' && styles.filterButtonActive
                  ]}
                  onPress={() => selectFilter('products')}
                >
                  <Text style={[
                    styles.filterButtonText,
                    serviceProductFilter === 'products' && styles.filterButtonTextActive
                  ]}>
                    Products{products.length > 0 ? ` (${products.length})` : ''}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Category pills — DoorDash-style, only worth showing when there's more than one category */}
            {catalogCategories.length > 1 && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                style={styles.categoryPillScroll}
                contentContainerStyle={styles.categoryPillScrollContent}
              >
                <TouchableOpacity
                  style={[
                    styles.categoryPill,
                    catalogCategoryFilter === 'all' && styles.categoryPillActive
                  ]}
                  onPress={() => setCatalogCategoryFilter('all')}
                >
                  <Text style={[
                    styles.categoryPillText,
                    catalogCategoryFilter === 'all' && styles.categoryPillTextActive
                  ]}>All</Text>
                </TouchableOpacity>
                {catalogCategories.map((cat) => (
                  <TouchableOpacity
                    key={cat}
                    style={[
                      styles.categoryPill,
                      catalogCategoryFilter === cat && styles.categoryPillActive
                    ]}
                    onPress={() => setCatalogCategoryFilter(cat)}
                  >
                    <Text style={[
                      styles.categoryPillText,
                      catalogCategoryFilter === cat && styles.categoryPillTextActive
                    ]}>{cat}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}
          </View>
        }
        ListEmptyComponent={
          <View style={styles.noResultsContainer}>
            <Text style={styles.noResultsIcon}>
              {serviceProductFilter === 'services' ? '💇‍♀️' : serviceProductFilter === 'products' ? '📦' : '🔍'}
            </Text>
            <Text style={styles.noResultsText}>
              No {serviceProductFilter === 'services' ? 'services' : serviceProductFilter === 'products' ? 'products' : 'items'} available
            </Text>
            <Text style={styles.noResultsSubtext}>
              {serviceProductFilter === 'services'
                ? 'This business does not offer services yet'
                : serviceProductFilter === 'products'
                ? 'This business does not offer products yet'
                : 'This business does not offer services or products yet'}
            </Text>
          </View>
        }
        ListFooterComponent={
          selectedItems.length > 0 ? (
            <View style={styles.continueButtonContainer}>
              <TouchableOpacity
                style={styles.continueButton}
                onPress={() => {
                  if (selectedItems.length > 0) {
                    setSelectedService(selectedItems[0]);
                    proceedAfterServiceSelection();
                  }
                }}
              >
                <Text style={styles.continueButtonText}>
                  Continue with {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null
        }
      />
    );
  };

  const renderBusinessSelection = () => (
    <View style={styles.stepContainer}>
      <Text style={styles.stepTitle}>Choose a BookAPT business</Text>
      <Text style={styles.stepSubtitle}>30-second booking is only available for businesses on BookAPT</Text>
      
      <View style={styles.businessesList}>
        {businesses.map((business) => (
          <TouchableOpacity
            key={business.id}
            style={[
              styles.businessCard,
              selectedBusiness?.id === business.id && styles.businessCardSelected
            ]}
            onPress={() => handleBusinessSelect(business)}
          >
            <View style={styles.businessInfo}>
              <Text style={styles.businessName}>{business.name}</Text>
              <Text style={styles.businessCategory}>{business.category}</Text>
              <View style={styles.businessRating}>
                <Text style={styles.ratingText}>⭐ {business.rating}</Text>
                <Text style={styles.distanceText}>📍 {business.distance}</Text>
              </View>
              <Text style={styles.businessAddress}>{business.address}</Text>
            </View>
            <View style={styles.businessActions}>
              {business.isFavorite && (
                <Text style={styles.favoriteIcon}>♥</Text>
              )}
              <Text style={styles.businessPrice}>{business.priceRange}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>
      
      {/* Button to explore more businesses - navigates directly to business exploration */}
      <View style={styles.exploreMoreContainer}>
        <TouchableOpacity
          style={styles.exploreMoreButton}
          onPress={() => {
            if (onNavigateToBusinessExploration) {
              // Store the selected category in a way that BusinessExplorationScreen can access it
              // We'll use localStorage/sessionStorage for web, or pass it through navigation
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                if (selectedCategory) {
                  sessionStorage.setItem('preselectedCategory', selectedCategory);
                }
              }
              onNavigateToBusinessExploration();
            }
          }}
        >
          <Text style={styles.exploreMoreButtonText}>🔍 Explore More Businesses</Text>
          <Text style={styles.exploreMoreButtonSubtext}>Choose services from all businesses</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderAvailabilitySelection = () => {
    // Generate next 7 days for date selection
    const getNextSevenDays = () => {
      const days = [];
      const today = new Date();
      
      // Calculate the target month
      const targetMonth = new Date(today);
      targetMonth.setMonth(today.getMonth() + selectedMonth);
      
      // For current month, start from today. For future months, start from 1st
      const startDate = selectedMonth === 0 ? new Date(today) : new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
      
      // Calculate end date (end of the target month)
      const endDate = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
      
      // Generate all dates in the target month
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
        const dateStr = formatLocalDateStr(currentDate);
        const displayDate = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!bookingBusinessHours || isBusinessOpenOnDate(bookingBusinessHours, dateStr)) {
          days.push({ dayName, dateStr, displayDate });
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return days;
    };

    const getMonthOptions = () => {
      const today = new Date();
      const months = [];
      
      for (let i = 0; i < 3; i++) {
        const monthDate = new Date(today);
        monthDate.setMonth(today.getMonth() + i);
        
        months.push({
          index: i,
          name: monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          shortName: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        });
      }
      
      return months;
    };

    const handleMonthSelect = (monthIndex: number) => {
      setSelectedMonth(monthIndex);
      setSelectedDate(''); // Clear selected date when changing months
      setSelectedTime(''); // Clear selected time
      setAvailableSlots([]); // Clear available slots
    };

    const availableDates = getNextSevenDays();
    console.log(`📅 Generated ${availableDates.length} dates for month ${selectedMonth}`);

    return (
      <View style={styles.stepContainer}>
        <Text style={styles.stepTitle}>Select Date & Time</Text>
        <Text style={styles.stepSubtitle}>Choose your preferred date and time slot</Text>
        
        <View style={styles.selectedBusinessInfo}>
          <Text style={styles.selectedBusinessName}>{selectedBusiness?.name}</Text>
          <Text style={styles.selectedBusinessService}>{selectedService?.serviceName}</Text>
        </View>

        {/* Month Selection */}
        <View style={styles.monthSelectionContainer}>
          <Text style={styles.sectionLabel}>Select Month</Text>
          <View style={styles.monthSelector}>
            {getMonthOptions().map((month) => (
              <TouchableOpacity
                key={month.index}
                style={[styles.monthButton, selectedMonth === month.index && styles.monthButtonSelected]}
                onPress={() => handleMonthSelect(month.index)}
              >
                <Text style={[styles.monthButtonText, selectedMonth === month.index && styles.monthButtonTextSelected]}>
                  {month.shortName}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Date Selection */}
        <View style={styles.dateSelectionContainer}>
          <Text style={styles.sectionLabel}>Select a Date</Text>
          <View style={styles.dateScrollContainer}>
            {/* Left Arrow */}
            <TouchableOpacity 
              style={styles.dateNavArrow}
              onPress={() => {
                if (dateScrollRef.current) {
                  const newPosition = Math.max(0, scrollPosition - 200);
                  setScrollPosition(newPosition);
                  dateScrollRef.current.scrollTo({ x: newPosition, animated: true });
                }
              }}
            >
              <Text style={styles.arrowText}>‹</Text>
            </TouchableOpacity>
            
            {/* Date Scroll View */}
            <ScrollView 
              ref={dateScrollRef}
              horizontal 
              showsHorizontalScrollIndicator={false} 
              style={styles.dateScrollView}
            >
              {availableDates.map((date) => (
                <TouchableOpacity
                  key={date.dateStr}
                  style={[
                    styles.dateCard,
                    selectedDate === date.dateStr && styles.dateCardSelected
                  ]}
                  onPress={async () => {
                    setSelectedDate(date.dateStr);
                    setSelectedTime(''); // Reset time when date changes
                    setSelectedWorker(null); // Reset worker selection
                    
                    // Reload available slots for the new date
                    if (selectedBusiness) {
                      await loadAvailableSlotsWithWorkers(selectedBusiness.id, date.dateStr);
                    }
                  }}
                >
                  <Text style={[
                    styles.dateDayName,
                    selectedDate === date.dateStr && styles.dateDayNameSelected
                  ]}>
                    {date.dayName}
                  </Text>
                  <Text style={[
                    styles.dateDisplay,
                    selectedDate === date.dateStr && styles.dateDisplaySelected
                  ]}>
                    {date.displayDate}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            
            {/* Right Arrow */}
            <TouchableOpacity 
              style={styles.dateNavArrow}
              onPress={() => {
                if (dateScrollRef.current) {
                  const newPosition = scrollPosition + 200;
                  setScrollPosition(newPosition);
                  dateScrollRef.current.scrollTo({ x: newPosition, animated: true });
                }
              }}
            >
              <Text style={styles.arrowText}>›</Text>
            </TouchableOpacity>
          </View>
        </View>
        
        {/* Time Selection */}
        {selectedDate && (
          <View style={styles.timeSelectionContainer}>
            <Text style={styles.sectionLabel}>
              Select a time for {selectedDate === getNextSevenDays()[0]?.dateStr ? 'Today' : selectedDate === getNextSevenDays()[1]?.dateStr ? 'Tomorrow' : selectedDate}
            </Text>
            {availableSlots.length === 0 && bookingBusinessHours && !isBusinessOpenOnDate(bookingBusinessHours, selectedDate) ? (
              <Text style={styles.closedDayMessage}>This business is closed on this day. Please choose another date.</Text>
            ) : (
            <View style={styles.timeSlotsGrid}>
              {availableSlots.map((timeSlot) => (
                <TouchableOpacity
                  key={timeSlot.time || timeSlot}
                  style={[
                    styles.timeSlot,
                    selectedTime === (timeSlot.time || timeSlot) && styles.timeSlotSelected
                  ]}
                  onPress={() => handleTimeSelect(timeSlot)}
                >
                  <Text style={[
                    styles.timeSlotText,
                    selectedTime === (timeSlot.time || timeSlot) && styles.timeSlotTextSelected
                  ]}>
                    {timeSlot.time || timeSlot}
                  </Text>
                  {timeSlot.availableWorkers && (
                    <Text style={styles.workerCountText}>
                      {timeSlot.availableWorkers.length} {timeSlot.availableWorkers.length === 1 ? 'worker' : 'workers'}
                    </Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderBookingConfirmation = () => {
    // Use selectedItems if available (multi-selection), otherwise use selectedService (single selection)
    // Also include selectedService if it's not already in selectedItems (for backward compatibility)
    let itemsToDisplay = selectedItems.length > 0 ? selectedItems : [];
    if (selectedService && !itemsToDisplay.find(item => item.id === selectedService.id)) {
      itemsToDisplay = [selectedService, ...itemsToDisplay];
    }
    // Calculate total price with quantities
    const totalPrice = itemsToDisplay.reduce((sum, item) => {
      const quantity = getItemQuantity(item.id) || 1;
      return sum + servicePriceForTotal(item.price) * quantity;
    }, 0);
    
    console.log('🔍 renderBookingConfirmation - selectedItems:', selectedItems.length);
    console.log('🔍 renderBookingConfirmation - selectedService:', selectedService);
    console.log('🔍 renderBookingConfirmation - itemsToDisplay:', itemsToDisplay.length);
    console.log('🔍 renderBookingConfirmation - totalPrice:', totalPrice);
    console.log('🔍 renderBookingConfirmation - selectedBusiness:', selectedBusiness?.name);
    console.log('🔍 renderBookingConfirmation - selectedWorker:', selectedWorker?.name);
    
    return (
      <View style={styles.stepContainer}>
        <Text style={styles.stepTitle}>Confirm your booking</Text>
        <Text style={styles.stepSubtitle}>Review details and complete payment</Text>

      {guestMode ? renderGuestContactSection() : null}
      
      {/* Worker Selection - show if multiple workers available */}
      {availableWorkers.length > 1 && !selectedWorker && (
        <View style={styles.workerSelectionContainer}>
          <Text style={styles.sectionLabel}>Select Your Preferred Worker (Optional)</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View style={styles.workersContainer}>
              {availableWorkers.map((worker) => (
                <TouchableOpacity
                  key={worker.id}
                  style={[
                    styles.workerCard,
                    selectedWorker?.id === worker.id && styles.workerCardSelected
                  ]}
                  onPress={() => handleWorkerSelect(worker)}
                >
                  <Text style={styles.workerIcon}>👤</Text>
                  <Text style={[
                    styles.workerName,
                    selectedWorker?.id === worker.id && styles.workerNameSelected
                  ]}>
                    {worker.name}
                  </Text>
                  {worker.specialties && (
                    <Text style={styles.workerSpecialty}>{worker.specialties}</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
      
      <View style={styles.bookingSummary}>
        {/* Show selected items (for multi-selection) or single service */}
        {itemsToDisplay.length > 1 ? (
          <View style={styles.contactInfoSection}>
            <Text style={styles.contactInfoTitle}>Selected Items ({itemsToDisplay.length})</Text>
            {itemsToDisplay.map((item, index) => {
              const quantity = getItemQuantity(item.id) || 1;
              const itemTotal = servicePriceForTotal(item.price) * quantity;
              const lineLabel =
                !item.isProduct && !hasListedServicePrice(item.price)
                  ? 'Price on request'
                  : `$${itemTotal.toFixed(2)}`;
              return (
                <View key={item.id || index} style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>
                    {item.serviceName}
                    {quantity > 1 && ` × ${quantity}`}:
                  </Text>
                  <Text style={styles.summaryValue}>{lineLabel}</Text>
                </View>
              );
            })}
            <View style={[styles.summaryRow, { marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: '#E1E5E9' }]}>
              <Text style={[styles.summaryLabel, { fontWeight: 'bold', fontSize: 18 }]}>Total:</Text>
              <Text style={[styles.summaryValue, { fontWeight: 'bold', fontSize: 18 }]}>
                {totalPrice > 0 ? `$${totalPrice.toFixed(2)}` : 'Price on request'}
              </Text>
            </View>
          </View>
        ) : (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Service:</Text>
            <Text style={styles.summaryValue}>{selectedService?.serviceName || itemsToDisplay[0]?.serviceName || 'No service selected'}</Text>
          </View>
        )}
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Business:</Text>
          <Text style={styles.summaryValue}>{selectedBusiness?.name}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Worker:</Text>
          <Text style={styles.summaryValue}>
            {selectedWorker ? `👤 ${selectedWorker.name}` : '👤 Will be assigned automatically'}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Date:</Text>
          <Text style={styles.summaryValue}>
            {fulfillmentType === 'delivery' ? 'Delivery (ASAP)' : selectedDate}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Time:</Text>
          <Text style={styles.summaryValue}>
            {fulfillmentType === 'delivery'
              ? deliveryQuote?.etaMinutes
                ? `~${deliveryQuote.etaMinutes} min`
                : 'After checkout'
              : selectedTime}
          </Text>
        </View>
        {fulfillmentType === 'delivery' && deliveryQuote?.success ? (
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Delivery fee</Text>
              <Text style={styles.summaryValue}>
                {formatCentsUsd(deliveryQuote.deliveryFeeCents)}
              </Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Service fee</Text>
              <Text style={styles.summaryValue}>
                {formatCentsUsd(deliveryQuote.serviceFeeCents)}
              </Text>
            </View>
            {deliveryTipCents > 0 ? (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Tip</Text>
                <Text style={styles.summaryValue}>{formatCentsUsd(deliveryTipCents)}</Text>
              </View>
            ) : null}
          </>
        ) : null}
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Total Price:</Text>
          <Text style={styles.summaryValue}>
            {fulfillmentType === 'delivery' && deliveryQuote?.success
              ? formatCentsUsd(deliveryQuote.totalCents)
              : totalPrice > 0
                ? `$${totalPrice.toFixed(2)}`
                : 'Price on request'}
          </Text>
        </View>

        {fulfillmentType === 'delivery' && selectedBusiness?.id ? (
          <DeliveryCheckoutPanel
            businessId={selectedBusiness.id}
            subtotalCents={computeCartSubtotalCents()}
            customerPhone={customerData.phone}
            customerEmail={customerData.email}
            userId={isRealBookingAccountId(userId) ? userId : undefined}
            userAddress={user?.address}
            onQuote={(quote, address, tip) => {
              setDeliveryQuote(quote);
              setDeliveryAddress(address);
              setDeliveryTipCents(tip);
            }}
          />
        ) : null}
        
        {/* Product Options - Show if product has selected options */}
        {(() => {
          console.log('🔍 renderBookingConfirmation - Checking for product options');
          console.log('🔍 itemsToDisplay:', itemsToDisplay.map(i => ({ id: i.id, name: i.serviceName, isProduct: i.isProduct })));
          console.log('🔍 itemSelectedOptions keys:', Object.keys(itemSelectedOptions));
          console.log('🔍 itemSelectedOptions:', itemSelectedOptions);
          
          // Check all items in itemsToDisplay for product options
          const itemsWithOptions = itemsToDisplay.filter(item => {
            const storedOptions = itemSelectedOptions[item.id];
            const hasOptions = item.isProduct && item.options && item.options.length > 0 && storedOptions && Object.keys(storedOptions).length > 0;
            console.log(`🔍 Item ${item.id} (${item.serviceName}): hasOptions=${hasOptions}, storedOptions=`, storedOptions);
            return hasOptions;
          });
          
          console.log('🔍 itemsWithOptions:', itemsWithOptions.length);
          if (itemsWithOptions.length === 0) return null;
          
          // Render options for each item that has options
          return itemsWithOptions.map((item) => {
            const storedOptions = itemSelectedOptions[item.id];
            if (!storedOptions || Object.keys(storedOptions).length === 0) return null;
            
            const optionItems = item.options.map((option: any) => {
              const selectedValueId = storedOptions[option.id];
              if (!selectedValueId) return null;
              const selectedValue = option.values?.find((v: any) => v.id === selectedValueId);
              if (!selectedValue) return null;
              return (
                <View key={option.id} style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>{option.name}:</Text>
                  <Text style={styles.summaryValue}>
                    {selectedValue.name}
                    {selectedValue.priceModifier && selectedValue.priceModifier !== 0 && (
                      <Text style={styles.priceModifierText}>
                        {' '}({selectedValue.priceModifier > 0 ? '+' : ''}${selectedValue.priceModifier.toFixed(2)})
                      </Text>
                    )}
                  </Text>
                </View>
              );
            }).filter(Boolean);
            
            if (optionItems.length === 0) return null;
            
            return (
              <View key={item.id} style={styles.contactInfoSection}>
                <Text style={styles.contactInfoTitle}>
                  {itemsToDisplay.length > 1 ? `${item.serviceName} - Selected Options` : 'Selected Options'}
                </Text>
                {optionItems}
              </View>
            );
          }).filter(Boolean);
        })()}

        {/* Automotive intake — required for auto care; optional vehicle garage for other categories */}
        {isAutomotiveBooking ? (
          <View style={styles.contactInfoSection}>
            <AutomotiveServiceIntakeForm
              value={automotiveIntake}
              onChange={handleAutomotiveIntakeChange}
              savedVehicles={canUseGarageInBooking ? vehicles : []}
              selectedSavedVehicleId={canUseGarageInBooking ? selectedAutomotiveVehicleId : null}
              onSelectSavedVehicle={
                canUseGarageInBooking ? handleSelectSavedVehicleForIntake : undefined
              }
              onChooseManualEntry={
                canUseGarageInBooking ? handleChooseManualAutomotiveEntry : undefined
              }
              onManageVehicles={
                canUseGarageInBooking && garageAccountUserId
                  ? () => setShowVehiclesModal(true)
                  : undefined
              }
            />
          </View>
        ) : !guestMode ? (
        <View style={styles.contactInfoSection}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={styles.contactInfoTitle}>Vehicle (Optional)</Text>
            <TouchableOpacity onPress={() => setShowVehiclesModal(true)} style={{ paddingVertical: 6 }}>
              <Text style={{ color: '#007AFF', fontWeight: '700' }}>Manage</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Selected:</Text>
            <Text style={styles.summaryValue}>
              {selectedVehicle ? formatVehicleLabel(selectedVehicle) : 'None'}
            </Text>
          </View>
          <Text style={styles.autoFilledText}>
            Saving a vehicle makes future bookings faster.
          </Text>
        </View>
        ) : null}

        {!guestMode ? (
        <View style={styles.contactInfoSection}>
          <Text style={styles.contactInfoTitle}>Contact Information</Text>
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Name:</Text>
              <Text style={styles.summaryValue}>{customerData.name || 'Not provided'}</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Phone:</Text>
              <Text style={styles.summaryValue}>{customerData.phone || 'Not provided'}</Text>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Email:</Text>
              <Text style={styles.summaryValue}>{customerData.email || 'Not provided'}</Text>
            </View>
            {customerData.name && customerData.phone && customerData.email && (
              <Text style={styles.autoFilledText}>✓ Pre-filled from your account</Text>
            )}
          </>
        </View>
        ) : null}
      </View>
      
      <BookingPaymentActions
        paymentRequiredAtBooking={
          fulfillmentType === 'delivery' || merchantPrepayRequired
        }
        showPayNow={
          fulfillmentType === 'delivery'
            ? !!(deliveryQuote?.success && deliveryAddress)
            : totalPrice > 0
        }
        payNowTotalLabel={
          fulfillmentType === 'delivery' && deliveryQuote?.success
            ? formatCentsUsd(deliveryQuote.totalCents)
            : totalPrice > 0
              ? `$${totalPrice.toFixed(2)}`
              : undefined
        }
        onPayNow={fulfillmentType === 'delivery' ? handleDeliveryPayNow : handleBookNow}
        onPayLater={
          fulfillmentType === 'delivery' || merchantPrepayRequired
            ? undefined
            : handleBookNowPayLater
        }
      />
    </View>
  );
  };

  const closeProductOptionsModal = () => {
    setShowProductOptionsModal(false);
    setProductOptionsQuantity(1);
    setSelectedProductOptions({});
  };

  const confirmProductOptions = () => {
    if (selectedService?.options) {
      const hasAllRequired = selectedService.options.every(
        (opt) => !opt.isRequired || selectedProductOptions[opt.id],
      );
      if (!hasAllRequired) {
        Alert.alert('Required Options', 'Please select all required options.');
        return;
      }
    }

    if (selectedService) {
      setItemQuantities((prev) => ({
        ...prev,
        [selectedService.id]: productOptionsQuantity,
      }));
      setItemSelectedOptions((prev) => ({
        ...prev,
        [selectedService.id]: { ...selectedProductOptions },
      }));
      if (!isItemSelected(selectedService.id)) {
        setSelectedItems([...selectedItems, selectedService]);
      }
    }

    setShowProductOptionsModal(false);
    setSelectedService(null);
    setSelectedProductOptions({});
    setProductOptionsQuantity(1);
  };

  const productOptionsImageUri =
    selectedService?.isProduct &&
    Array.isArray(selectedService.images) &&
    selectedService.images.length > 0
      ? String((selectedService.imageThumbnails && selectedService.imageThumbnails[0]) || selectedService.images[0] || '').trim()
      : '';

  const renderProductOptionsSheet = () => (
    <View style={styles.modalOverlay}>
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <View style={styles.modalTitleRow}>
            {productOptionsImageUri ? (
              <Image
                source={{ uri: productOptionsImageUri }}
                style={styles.modalProductThumb}
                resizeMode="cover"
                {...(Platform.OS === 'web' ? { referrerPolicy: 'no-referrer' as const } : {})}
              />
            ) : null}
            <Text style={styles.modalTitle}>
              {selectedService?.serviceName || 'Product Options'}
            </Text>
          </View>
          <TouchableOpacity onPress={closeProductOptionsModal}>
            <Text style={styles.modalCloseButton}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.modalBody}>
          <View style={styles.optionGroup}>
            <Text style={styles.optionLabel}>Quantity</Text>
            <View style={styles.quantityControls}>
              <TouchableOpacity
                style={styles.quantityButton}
                onPress={() => {
                  if (productOptionsQuantity > 1) {
                    setProductOptionsQuantity(productOptionsQuantity - 1);
                  }
                }}
              >
                <Text style={styles.quantityButtonText}>−</Text>
              </TouchableOpacity>
              <Text style={styles.quantityText}>{productOptionsQuantity}</Text>
              <TouchableOpacity
                style={styles.quantityButton}
                onPress={() => {
                  setProductOptionsQuantity(productOptionsQuantity + 1);
                }}
              >
                <Text style={styles.quantityButtonText}>+</Text>
              </TouchableOpacity>
            </View>
          </View>

          {selectedService?.options && selectedService.options.length > 0 ? (
            selectedService.options.map((option) => (
              <View key={option.id} style={styles.optionGroup}>
                <Text style={styles.optionLabel}>
                  {option.name}
                  {option.isRequired ? (
                    <Text style={styles.requiredAsterisk}> *</Text>
                  ) : (
                    <Text style={styles.optionalOptionLabel}> (Optional)</Text>
                  )}
                </Text>
                <View style={styles.optionValues}>
                  {option.values.map((value) => {
                    const isSelected = selectedProductOptions[option.id] === value.id;
                    return (
                      <TouchableOpacity
                        key={value.id}
                        style={[
                          styles.optionValueButton,
                          isSelected && styles.optionValueButtonSelected,
                        ]}
                        onPress={() => {
                          setSelectedProductOptions({
                            ...selectedProductOptions,
                            [option.id]: value.id,
                          });
                        }}
                      >
                        <Text
                          style={[
                            styles.optionValueText,
                            isSelected && styles.optionValueTextSelected,
                          ]}
                        >
                          {value.name}
                          {value.priceModifier && value.priceModifier !== 0 ? (
                            <Text style={styles.priceModifierText}>
                              {' '}
                              ({value.priceModifier > 0 ? '+' : ''}$
                              {value.priceModifier.toFixed(2)})
                            </Text>
                          ) : null}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.optionEmptyText}>No customizable options for this product.</Text>
          )}
        </ScrollView>

        <View style={styles.modalFooter}>
          <TouchableOpacity style={styles.cancelButton} onPress={closeProductOptionsModal}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.confirmButton,
              (!selectedService?.options ||
                selectedService.options.some(
                  (opt) => opt.isRequired && !selectedProductOptions[opt.id],
                )) &&
                styles.confirmButtonDisabled,
            ]}
            onPress={confirmProductOptions}
            disabled={selectedService?.options?.some(
              (opt) => opt.isRequired && !selectedProductOptions[opt.id],
            )}
          >
            <Text style={styles.confirmButtonText}>Continue</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={goBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>
            {guestMode ? 'Book Appointment' : '30-Second Booking'}
          </Text>
          {!guestMode ? (
            <Text style={styles.headerSubtitle}>For BookAPT businesses only</Text>
          ) : null}
        </View>
        <View style={styles.headerSpacer} />
      </View>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Step Content */}
      {currentStep === 'service' ? (
        // Owns its own FlatList scroll (virtualized grid) instead of nesting inside the ScrollView below.
        renderServiceSelection()
      ) : (
        <ScrollView
          style={styles.stepContent}
          contentContainerStyle={styles.stepContentContainer}
          scrollEnabled={true}
          showsVerticalScrollIndicator={true}
        >
          {currentStep === 'category' && renderCategorySelection()}
          {currentStep === 'business' && renderBusinessSelection()}
          {currentStep === 'fulfillment' && renderFulfillmentSelection()}
          {currentStep === 'availability' && renderAvailabilitySelection()}
          {currentStep === 'booking' && renderBookingConfirmation()}
        </ScrollView>
      )}

      {/* Footer with navigation and confirm button */}
      <View style={styles.footer}>
        <TouchableOpacity style={styles.backButton} onPress={goBack}>
          <Text style={styles.backButtonText}>
            {currentStep === 'category' ? 'Close' : 'Back'}
          </Text>
        </TouchableOpacity>
          {currentStep === 'availability' && (
          <TouchableOpacity 
            style={[
              styles.confirmButton,
              (!selectedDate || !selectedTime) && styles.confirmButtonDisabled
            ]} 
            onPress={() => setCurrentStep('booking')}
            disabled={!selectedDate || !selectedTime}
          >
            <Text style={styles.confirmButtonText}>Continue</Text>
          </TouchableOpacity>
        )}
      </View>

      <MyVehiclesModal
        visible={showVehiclesModal}
        userId={garageAccountUserId || userId}
        selectedVehicleId={selectedVehicleId}
        onSelectVehicleId={async (id) => {
          setSelectedVehicleId(id);
          setShowVehiclesModal(false);
          const accountId = garageAccountUserId || userId;
          try {
            const list = await vehicleGarageService.listVehicles(accountId);
            setVehicles(list);
            if (isAutomotiveBooking) {
              const vehicle = list.find((v) => v.id === id);
              if (vehicle) {
                handleSelectSavedVehicleForIntake(vehicle);
              }
            }
          } catch (_) {
            // ignore
          }
        }}
        onClose={async () => {
          setShowVehiclesModal(false);
          const accountId = garageAccountUserId || userId;
          try {
            const list = await vehicleGarageService.listVehicles(accountId);
            setVehicles(list);
          } catch (_) {
            // ignore
          }
        }}
      />

      {/* Payment Modal */}
      <Modal
        visible={showPaymentModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          console.log('🔵 Payment Modal: onRequestClose called');
          setShowPaymentModal(false);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.paymentModal}>
            {console.log('🔵 Payment Modal: Rendering modal, visible:', showPaymentModal)}
            <Text style={styles.paymentModalTitle}>Select Payment Method</Text>
            <Text style={styles.paymentModalSubtitle}>Choose how you'd like to pay</Text>
            
            <View style={styles.paymentMethodsList}>
              {availablePaymentMethods.map((method) => (
                <TouchableOpacity
                  key={method.id}
                  style={[
                    styles.paymentMethodItem,
                    selectedPaymentMethod === method.id && styles.paymentMethodSelected
                  ]}
                  onPress={() => handlePaymentMethodSelect(method.id)}
                >
                  <View style={styles.paymentMethodIconWrap}>
                    <PaymentMethodBrandIcon methodId={method.id} size={36} />
                  </View>
                  <Text style={styles.paymentMethodName}>{method.name}</Text>
                  {selectedPaymentMethod === method.id && (
                    <Text style={styles.paymentMethodCheck}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.paymentModalActions}>
              <TouchableOpacity
                style={styles.paymentCancelButton}
                onPress={() => setShowPaymentModal(false)}
              >
                <Text style={styles.paymentCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.paymentConfirmButton,
                  !selectedPaymentMethod && styles.paymentConfirmButtonDisabled
                ]}
                onPress={processBooking}
                disabled={!selectedPaymentMethod}
              >
              <Text style={styles.paymentConfirmButtonText}>
                {loading ? 'Processing Payment...' : 'Pay & Book Now'}
              </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Product Options Modal — inline overlay in widget iframe (RN Modal can fail to appear) */}
      {isWidgetEmbedContext() && Platform.OS === 'web' ? (
        showProductOptionsModal ? (
          <View style={styles.embedModalLayer}>{renderProductOptionsSheet()}</View>
        ) : null
      ) : (
        <Modal
          visible={showProductOptionsModal}
          animationType="slide"
          transparent={true}
          onRequestClose={closeProductOptionsModal}
        >
          {renderProductOptionsSheet()}
        </Modal>
      )}

      {/* Appointment Limit Modal */}
      <AppointmentLimitModal
        visible={showLimitModal}
        limitCheck={limitCheck}
        onUpgrade={() => {
          setShowLimitModal(false);
          Alert.alert(
            'Upgrade Required',
            'Please upgrade your subscription to continue booking.',
            [{ text: 'OK' }]
          );
        }}
        onCancel={() => {
          setShowLimitModal(false);
          setLimitCheck(null);
        }}
      />

      {/* Web Stripe Checkout Modal (Apple Pay / Google Pay / Card) */}
      <WebStripeCheckoutModal
        visible={webCheckoutVisible}
        intentInput={webCheckoutInput}
        preferredMethodLabel={webCheckoutMethodLabel}
        onClose={() => finishWebCheckout({ success: false, cancelled: true, error: 'Payment cancelled' })}
        onResult={(r) => finishWebCheckout(r)}
      />

      <WebStripeDeliveryCheckoutModal
        visible={deliveryCheckoutVisible}
        checkout={deliveryCheckoutData}
        onClose={() => {
          setDeliveryCheckoutVisible(false);
          setDeliveryCheckoutData(null);
        }}
        onResult={handleDeliveryCheckoutResult}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    paddingTop: 50,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 122, 255, 0.1)',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 11,
    color: '#666',
    textAlign: 'center',
    marginTop: 2,
  },
  headerSpacer: {
    width: 60,
  },
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  stepContainer: {
    alignItems: 'center',
    flex: 1,
  },
  stepCircle: {
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  stepCircleActive: {
    backgroundColor: '#007AFF',
  },
  stepCircleInactive: {
    backgroundColor: '#e1e5e9',
  },
  stepNumber: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  stepNumberActive: {
    color: 'white',
  },
  stepNumberInactive: {
    color: '#8E8E93',
  },
  stepLabel: {
    fontSize: 12,
    textAlign: 'center',
  },
  stepLabelActive: {
    color: '#007AFF',
    fontWeight: '600',
  },
  stepLabelInactive: {
    color: '#8E8E93',
  },
  stepContent: {
    flex: 1,
  },
  stepContentContainer: {
    paddingBottom: 80, // Space for bottom menu bar
  },
  stepContainer: {
    padding: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 16,
    margin: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    marginBottom: 24,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  searchIcon: {
    fontSize: 20,
    marginRight: 12,
    opacity: 0.6,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
    paddingVertical: 0,
  },
  clearSearchButton: {
    padding: 4,
    marginLeft: 8,
  },
  clearSearchText: {
    fontSize: 18,
    color: '#8E8E93',
    fontWeight: 'bold',
  },
  searchResultsText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '600',
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  noResultsContainer: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  noResultsIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5,
  },
  noResultsText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsSubtext: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 20,
  },
  categoriesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  categoryCard: {
    width: '48%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    padding: 20,
    marginBottom: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(0, 122, 255, 0.1)',
  },
  categoryCardSelected: {
    backgroundColor: 'rgba(0, 122, 255, 0.1)',
    borderWidth: 2,
    borderColor: '#007AFF',
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  categoryIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
    textAlign: 'center',
  },
  categoryDescription: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 16,
  },
  loadMoreButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  loadMoreButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginRight: 8,
  },
  loadMoreIcon: {
    fontSize: 14,
    color: 'white',
  },
  showLessButton: {
    backgroundColor: '#6C757D',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  showLessButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginRight: 8,
  },
  showLessIcon: {
    fontSize: 14,
    color: 'white',
  },
  servicesList: {
    gap: 12,
  },
  serviceCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(37, 99, 235, 0.1)',
    marginBottom: 12,
  },
  serviceCardSelected: {
    backgroundColor: 'rgba(37, 99, 235, 0.1)',
    borderWidth: 2,
    borderColor: '#2563EB',
    shadowColor: '#2563EB',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  serviceCardContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxContainer: {
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#2563EB',
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  checkboxCheckmark: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#2563EB',
    marginLeft: 12,
  },
  addButtonSelected: {
    backgroundColor: '#EF4444',
  },
  addButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  addButtonTextSelected: {
    color: 'white',
  },
  quantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
    gap: 8,
  },
  quantityButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#2563EB',
  },
  quantityButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  quantityText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    minWidth: 24,
    textAlign: 'center',
  },
  addQuantityButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#2563EB',
  },
  addQuantityButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  selectedItemsBadge: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    alignSelf: 'flex-start',
    marginBottom: 16,
  },
  selectedItemsBadgeText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  continueButtonContainer: {
    padding: 20,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
  },
  continueButton: {
    backgroundColor: '#2563EB',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  serviceInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  serviceIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  productThumb: {
    width: 48,
    height: 48,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: '#e9ecef',
  },
  serviceDetails: {
    flex: 1,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  serviceDescription: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 8,
  },
  serviceMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  serviceStock: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  outOfStockText: {
    color: '#EF4444',
  },
  gridRow: {
    gap: Platform.OS === 'web' ? 8 : 12,
    paddingHorizontal: 16,
  },
  gridCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(37, 99, 235, 0.1)',
    marginBottom: Platform.OS === 'web' ? 8 : 12,
  },
  gridImageWrap: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: '#e9ecef',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridImage: {
    width: '100%',
    height: '100%',
  },
  gridIcon: {
    fontSize: Platform.OS === 'web' ? 28 : 40,
  },
  gridPopularBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    marginLeft: 0,
  },
  gridCardBody: {
    padding: Platform.OS === 'web' ? 8 : 10,
  },
  gridCardName: {
    fontSize: Platform.OS === 'web' ? 12 : 14,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
    minHeight: Platform.OS === 'web' ? 30 : 36,
  },
  gridCardMeta: {
    fontSize: Platform.OS === 'web' ? 11 : 12,
    color: '#8E8E93',
    marginBottom: 6,
  },
  gridCardFooterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  gridCardPrice: {
    fontSize: Platform.OS === 'web' ? 13 : 15,
    fontWeight: 'bold',
    color: '#34C759',
  },
  gridQuantityControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  gridQuantityButton: {
    width: Platform.OS === 'web' ? 22 : 26,
    height: Platform.OS === 'web' ? 22 : 26,
    borderRadius: 11,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridAddButton: {
    width: Platform.OS === 'web' ? 24 : 28,
    height: Platform.OS === 'web' ? 24 : 28,
    borderRadius: 12,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gridAddButtonText: {
    color: 'white',
    fontSize: Platform.OS === 'web' ? 16 : 18,
    fontWeight: 'bold',
    lineHeight: 20,
  },
  categoryPillScroll: {
    marginBottom: 12,
  },
  categoryPillScrollContent: {
    gap: 8,
    paddingRight: 16,
  },
  categoryPill: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  categoryPillActive: {
    backgroundColor: '#1C1C1E',
    borderColor: '#1C1C1E',
  },
  categoryPillText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
  },
  categoryPillTextActive: {
    color: 'white',
  },
  filterSelector: {
    marginTop: 12,
    marginBottom: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  filterButtons: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  filterButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
  },
  filterButtonTextActive: {
    color: 'white',
  },
  serviceDuration: {
    fontSize: 12,
    color: '#8E8E93',
  },
  servicePrice: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#34C759',
  },
  popularBadge: {
    backgroundColor: '#FF9500',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 8,
  },
  popularText: {
    fontSize: 12,
    color: 'white',
    fontWeight: 'bold',
  },
  businessesList: {
    gap: 12,
  },
  businessCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(0, 122, 255, 0.1)',
  },
  businessCardSelected: {
    backgroundColor: 'rgba(0, 122, 255, 0.1)',
    borderWidth: 2,
    borderColor: '#007AFF',
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  businessInfo: {
    flex: 1,
  },
  businessName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  businessCategory: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 8,
  },
  businessRating: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  ratingText: {
    fontSize: 14,
    color: '#FFC107',
    fontWeight: '600',
    marginRight: 8,
  },
  distanceText: {
    fontSize: 14,
    color: '#8E8E93',
  },
  businessAddress: {
    fontSize: 12,
    color: '#8E8E93',
  },
  businessActions: {
    alignItems: 'flex-end',
  },
  favoriteIcon: {
    fontSize: 20,
    color: '#FF3B30',
    marginBottom: 4,
  },
  businessPrice: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#34C759',
  },
  exploreMoreContainer: {
    marginTop: 20,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  exploreMoreButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  exploreMoreButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  exploreMoreButtonSubtext: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  selectedBusinessInfo: {
    backgroundColor: '#F2F2F7',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  selectedBusinessName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  selectedBusinessService: {
    fontSize: 14,
    color: '#8E8E93',
  },
  dateSelectionContainer: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  dateScrollContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateNavArrow: {
    width: 35,
    height: 35,
    borderRadius: 17,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  arrowText: {
    fontSize: 20,
    color: 'white',
    fontWeight: 'bold',
  },
  dateScrollView: {
    flex: 1,
    marginBottom: 8,
  },
  dateCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginRight: 12,
    alignItems: 'center',
    minWidth: 90,
    borderWidth: 2,
    borderColor: '#E1E5E9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  dateCardSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },
  dateDayName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#8E8E93',
    marginBottom: 4,
  },
  dateDayNameSelected: {
    color: 'white',
  },
  dateDisplay: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  dateDisplaySelected: {
    color: 'white',
  },
  timeSelectionContainer: {
    marginTop: 16,
  },
  timeSlotsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  timeSlot: {
    width: '30%',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  timeSlotSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  timeSlotText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  timeSlotTextSelected: {
    color: 'white',
  },
  workerCountText: {
    fontSize: 10,
    color: '#8E8E93',
    marginTop: 4,
  },
  closedDayMessage: {
    fontSize: 15,
    color: '#8E8E93',
    marginTop: 8,
    lineHeight: 22,
  },
  workerSelectionContainer: {
    marginBottom: 20,
  },
  workersContainer: {
    flexDirection: 'row',
    paddingRight: 20,
  },
  workerCard: {
    width: 120,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginRight: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#E1E5E9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  workerCardSelected: {
    backgroundColor: 'rgba(0, 122, 255, 0.1)',
    borderColor: '#007AFF',
    borderWidth: 2,
    shadowColor: '#007AFF',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 4,
  },
  workerIcon: {
    fontSize: 32,
    marginBottom: 8,
  },
  workerName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 4,
  },
  workerNameSelected: {
    color: '#007AFF',
  },
  workerSpecialty: {
    fontSize: 11,
    color: '#8E8E93',
    textAlign: 'center',
    lineHeight: 14,
  },
  bookingSummary: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  summaryLabel: {
    fontSize: 16,
    color: '#8E8E93',
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  contactInfoSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  contactInfoTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  autoFilledText: {
    fontSize: 14,
    color: '#34C759',
    fontWeight: '600',
    marginTop: 8,
    fontStyle: 'italic',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
    }),
  },
  paymentModal: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 24,
    margin: 20,
    width: '90%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 10000,
      maxHeight: '90vh',
      overflow: 'auto' as any,
    }),
  },
  paymentModalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 8,
  },
  paymentModalSubtitle: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 24,
  },
  paymentMethodsList: {
    marginBottom: 24,
  },
  paymentMethodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  paymentMethodSelected: {
    backgroundColor: 'rgba(0, 122, 255, 0.1)',
    borderColor: '#007AFF',
    borderWidth: 2,
  },
  paymentMethodIconWrap: {
    width: 40,
    height: 40,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentMethodName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  paymentMethodCheck: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
  },
  paymentModalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  paymentCancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e1e5e9',
    alignItems: 'center',
  },
  paymentCancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8E8E93',
  },
  paymentConfirmButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: '#007AFF',
    alignItems: 'center',
  },
  paymentConfirmButtonDisabled: {
    backgroundColor: '#e1e5e9',
  },
  paymentConfirmButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
  },
  // Month Selection Styles
  monthSelectionContainer: {
    marginBottom: 20,
  },
  monthSelector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  monthButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginHorizontal: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: 'white',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  monthButtonSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  monthButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  monthButtonTextSelected: {
    color: 'white',
  },
  // Footer styles
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: 'white',
  },
  backButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  backButtonText: {
    fontSize: 16,
    color: '#666',
  },
  confirmButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#007AFF',
    borderRadius: 8,
  },
  confirmButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
  },
  confirmButtonDisabled: {
    backgroundColor: '#ccc',
    opacity: 0.6,
  },
  // Product Options Modal Styles
  embedModalLayer: {
    ...(Platform.OS === 'web'
      ? {
          position: 'fixed' as any,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
        }
      : {
          ...StyleSheet.absoluteFillObject,
          zIndex: 99999,
        }),
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
    ...(Platform.OS === 'web' && {
      position: 'relative' as any,
      zIndex: 10000,
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  modalTitleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  modalProductThumb: {
    width: 40,
    height: 40,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: '#e9ecef',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  modalCloseButton: {
    fontSize: 24,
    color: '#8E8E93',
    fontWeight: '300',
  },
  modalBody: {
    maxHeight: 400,
    padding: 20,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e1e5e9',
    gap: 12,
  },
  optionGroup: {
    marginBottom: 24,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 12,
  },
  requiredAsterisk: {
    color: '#EF4444',
  },
  optionalOptionLabel: {
    color: '#8E8E93',
    fontWeight: '400',
    fontSize: 14,
  },
  optionEmptyText: {
    fontSize: 14,
    color: '#8E8E93',
    fontStyle: 'italic',
  },
  optionValues: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionValueButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e5e9',
    backgroundColor: '#f8f9fa',
  },
  optionValueButtonSelected: {
    borderColor: '#2563EB',
    backgroundColor: '#EFF6FF',
  },
  optionValueText: {
    fontSize: 14,
    color: '#1C1C1E',
    fontWeight: '500',
  },
  optionValueTextSelected: {
    color: '#2563EB',
    fontWeight: '600',
  },
  priceModifierText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '400',
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  guestInput: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 10,
    backgroundColor: '#fff',
    color: '#1C1C1E',
  },
  guestAuthToggleRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  guestAuthToggleBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  guestAuthToggleBtnActive: {
    borderColor: '#667eea',
    backgroundColor: '#EEF0FF',
  },
  guestAuthToggleText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  guestAuthToggleTextActive: {
    color: '#667eea',
  },
  guestAuthError: {
    color: '#dc3545',
    fontSize: 13,
    marginTop: 4,
  },
  guestPasswordRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 10,
    backgroundColor: '#fff',
  },
  guestPasswordInput: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
  },
  guestPasswordEyeBtn: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  guestPasswordEyeIcon: {
    fontSize: 18,
  },
});

export default OneClickBookingScreen;
